import * as ko from 'knockout';
import i18n from '../i18n';

import { parseDate, parseDateTime, serializeDate, serializeNumber } from '../api/serialization';
import {
  TrialWizardData,
  DatasetWizardData,
  TrialData,
  WalkOrder,
  StartingCorner,
  PlotNaming,
  CustomLayoutData,
  PlotGuidesData,
  EditMode,
  TrialTraitData,
  RangeDirection,
  WarningMessageType,
  WARNING_MESSAGES_TYPES,
  DataEntryHeaderData,
  TrialInputApplicationData,
} from '../api/trials';
import { DimensionData, listForDMS } from '../api/dimensions';
import { CountryData } from '../api/countries';
import { RegionData } from '../api/regions';
import * as datasetsApi from '../api/datasets';
import * as sitesApi from '../api/sites';
import * as dimensionMetasApi from '../api/dimension_metas';

import { DimensionMeta } from './dimension_meta';
import { DatasetDimensionMeta, LimitToDimension } from './dataset_dimension_meta';
import { Treatment } from './treatment';
import { TraitAction } from './trait_action';
import { MeasurementMeta } from './measurement_meta';
import { OrderedEntities } from './helpers/ordered_entities';
import { DatasetDimensionMetasEditConfiguration } from '../components/dataset_dimension_metas_edit';
import { slugValidation, SlugGenerator, generateSlug } from '../ko_bindings/slug_validation';
import { confirmDialog } from '../components/confirm_dialog';
import { I18nText, asI18nText, transformI18Text, translate } from '../i18n_text';
import { UserData } from '../api/users';
import { SITE_SLUG } from '../api/dimension_metas';
import { AgroRegionData } from '../api/agro_regions';
import { SeasonData } from '../api/seasons';
import { allowViewLimitToDimensionNameAndAttributes, canEditMMLibrary, canEditTrial } from '../permissions';
import { TPPListData } from '../api/tpps';
import { CropData } from '../api/crops';
import { NameData, NameI18nData } from '../api/names';
import { infoDialog } from '../components/info_dialog';
import { FormSelectSearchConfiguration } from '../components/form_select_search';
import { indexOf, emptyToNull, groupBy, accList, tryFormatDateShort, asInt } from '../utils';
import { FormNestedEntitiesConfiguration } from '../components/form_nested_entities';
import { TrialTypeData } from '../api/trial_types';
import { PlotsEditModel } from '../components/trial_wizard/plots_edit/plots_edit_model';
import { session } from '../session';
import { ListRequestParams } from '../api/request';
import {
  listTrialScheduledVisitsPreviews,
  listTrialScheduledVisitsTemplateDates,
  ScheduledVisitData,
  ScheduledVisitPreviewData,
  ScheduledVisitTraitData,
} from '../api/scheduled_visits';
import { ScheduledVisitBase } from './scheduled_visit';
import { typeName } from '../screens/measurement_meta_library';
import { SelectedTraitData, listDependencies, listTrialLibrary } from '../api/measurement_metas';

import { MeasurementMetaData } from '../api/v2/interfaces';
import { chunk } from 'lodash';
import { TrialState, getTrialStateLabel } from './TrialState';
import { logError } from '../error_logging';
import { MAX_DECIMALS, MAX_DIGITS } from './value_meta';

export function getLocationAccuracies() {
  return [
    { value: '-1', title: i18n.t('No minimum accuracy')() },
    { value: '1000', title: i18n.t('Up to 1000 meters')() },
    { value: '100', title: i18n.t('Up to 100 meters')() },
    { value: '20', title: i18n.t('Up to 20 meters')() },
    { value: '10', title: i18n.t('Up to 10 meters')() },
    { value: '5', title: i18n.t('Up to 5 meters')() },
  ];
}

export function getTrialEditModes(): { value: EditMode; title: string }[] {
  return [
    { value: 'manual', title: i18n.t('Manual')() },
    { value: 'library', title: i18n.t('Library')() },
  ];
}

export const CUSTOM_LAYOUT_VALID_PLOT_DESIGNS = ['default', 'rcb', 'crd', 'manual', 'split_plot'];
export const KEEP_TEST_SUBJECTS_PLOTS_COUNT_LIMIT = 100;
// Largest value the server's integer columns can store.
const MAX_INTEGER_VALUE = 2 ** 31 - 1;

// A function, not a constant: value_meta imports this module, so its constants are not yet
// initialized while this module loads.
function maxDecimalValue(): number {
  return 10 ** (MAX_DIGITS - MAX_DECIMALS) - 1;
}

export class Trial {
  private slugGenerator: SlugGenerator | null = null;

  randomizationSeed: number;

  private validateRcbSquare = {
    onlyIf: () => this.plotDesign() === 'rcb' && this.rcbType() === 'rcb_rect',
  };

  autoNameTrials: boolean;
  editMode: EditMode = 'library';

  id = ko.observable<string>(null);
  nameJson = ko.observable<I18nText>().extend({ serverError: true });
  nameSuffix = ko.observable('').extend({
    pattern: {
      params: '^[a-zA-Z0-9_]+$',
      message: i18n.t('Can only contain letters, numbers and underscores')(),
    },
  });
  nameSlug = ko.observable('').extend(slugValidation);
  state = ko.observable(TrialState.Draft).extend({
    required: true,
  });
  archived = ko.observable<boolean>(false);
  glpFieldRestricted = ko.observable<boolean>(false);

  showUnarchivedBadge = ko.pureComputed(() => this.glpFieldRestricted() && !this.archived());
  archivedBadgeTitle = i18n.t('This trial is archived');
  unarchivedBadgeTitle = i18n.t(
    'This trial was archived before, so staff users and mobile devices can no longer access it'
  );
  cancellationReason = ko.observable<string>('');
  stateChangeReason = ko.observable<string>('');
  plotNaming = ko.observable<PlotNaming>('default').extend({
    required: true,
    serverError: true,
  });
  rangeDirection = ko.observable<RangeDirection>('top_down').extend({
    required: true,
  });
  plotDesign = ko.observable('default').extend({
    required: true,
    serverError: true,
  });
  rcbType = ko.observable<datasetsApi.RCBType>(null);
  rowLength = ko.observable('').extend({
    digit: true,
    min: 1,
    max: MAX_INTEGER_VALUE,
  });
  rcbBlockWidth = ko.observable('').extend({
    digit: true,
    min: 1,
    max: MAX_INTEGER_VALUE,
    required: this.validateRcbSquare,
  });
  rcbBlocksPerRow = ko.observable('').extend({
    digit: true,
    min: 1,
    max: MAX_INTEGER_VALUE,
    required: this.validateRcbSquare,
  });
  separateTestSubjects = ko.observable(false);
  rcbFirstBlockInTreatmentOrder = ko.observable(false);
  startingCorner = ko.observable<StartingCorner>('top_left');
  walkOrder = ko.observable<WalkOrder>('top_down');
  trialType = ko.observable<TrialTypeData>(null);
  descriptionJson = ko.observable<I18nText>().extend({
    serverError: true,
  });
  plants = ko.observable<number>(null).extend({
    digit: true,
    min: 0,
    max: MAX_INTEGER_VALUE,
  });
  spaceBetweenRows = ko.observable<number>(null).extend({
    number: true,
    min: 0,
    max: maxDecimalValue(),
  });
  spaceInsideRows = ko.observable<number>(null).extend({
    number: true,
    min: 0,
    max: maxDecimalValue(),
  });
  squareMetersPerPlot = ko.observable<number>(null).extend({
    number: true,
    min: 0,
    max: maxDecimalValue(),
  });
  rowsPerPlot = ko.observable<number>(null).extend({
    digit: true,
    min: 0,
    max: MAX_INTEGER_VALUE,
  });
  borderJson = ko.observable<I18nText>().extend({
    serverError: true,
  });
  fieldSelectionJson = ko.observable<I18nText>().extend({
    serverError: true,
  });
  plotLength = ko.observable<number>(null).extend({
    number: true,
    min: 0,
    max: maxDecimalValue(),
  });
  plotWidth = ko.observable<number>(null).extend({
    number: true,
    min: 0,
    max: maxDecimalValue(),
  });
  scheduledPlantingDate = ko.observable<Date>(null);
  crop = ko.observable<CropData>(null);
  createdAt = ko.observable<Date | null>(null);
  cultivationType = ko.observable<DimensionData>(null);
  fieldPreparationType = ko.observable<DimensionData>(null);
  irrigationType = ko.observable<DimensionData>(null);
  country = ko.observable<CountryData>(null);
  region = ko.observable<RegionData>(null);
  agroRegion = ko.observable<AgroRegionData>(null);
  globalRegion = ko.observable<NameI18nData>(null);
  season = ko.observable<SeasonData>(null);
  customers = ko.observableArray<NameData>(null);
  protocol = ko.observable('');
  isCommercial = ko.observable(false);
  lastMeasurement = ko.observable<Date>(null);
  lastStaff = '';
  /** The names of the trial's sites' partners, as the list endpoint reports them. */
  sitePartnerNames: string[] = [];
  collectedMeasurements = ko.observable(0);
  measurementsToCollect = ko.observable(0);
  measurementsCalulationsAvailable = ko.observable(true);
  dataCollectionStatus = ko.observable<'done' | 'in_progress' | 'late' | 'calculating'>('done');
  measurementsToCollectByToday = ko.observable(0);
  measurementsOptional = ko.observable(0);
  modified = ko.observable<Date>(null);
  assigned = ko.observable(false);
  isLegacyTrial = false;
  enableBidirSync = ko.observable(true);
  disablePicturesFromGallery = ko.observable(false);
  wasCreatedUsingTreatmentsFeature = ko.observable(false);
  requiredLocationAccuracy = ko.observable(getLocationAccuracies()[0].value);
  warnLocationAccuracy = ko.observable(false);
  changeReasonPreference = ko.observable(false);
  defaultOverviewPresetId = ko.observable<number | null>(null);
  owners = ko.observableArray<UserData>().extend({
    required: true,
  });
  tpp = ko.observable<TPPListData>(null);
  scheduledVisits = ko.observableArray<ScheduledVisitData>(null);
  traits = ko.observableArray<MeasurementMetaData>(null);
  sites = ko.observableArray<DimensionData>(null);
  project = ko.observable<NameData>(null);
  partner = ko.observable<NameData>(null);
  matchedFields = ko.observableArray<{ field: string; value: string }>([]);
  gateInnovationId = ko.observable<number>(null);
  gateInnovationName = ko.observable<string>(null);
  template = false;
  canEditMMLibrary = false;
  createdFromTemplateId: string = null;

  private subscriptions: KnockoutSubscription[] = [];

  displayName = ko.pureComputed(() => {
    return translate(this.nameJson());
  });

  editUrl = ko.pureComputed(() => {
    if (this.isLegacyTrial) {
      return '';
    } else {
      return '/trials/' + this.id() + '/';
    }
  });
  formattedScheduledPlantingDate = ko.pureComputed(() => {
    return tryFormatDateShort(this.scheduledPlantingDate());
  });
  datasetsUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/datasets/';
  });

  exportTrialUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/export/';
  });

  assignStaffUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/assign_staff/';
  });

  accessUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/access/';
  });

  assignGroupsUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/assign/';
  });

  copyUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/copy/';
  });

  dashboardUrl = ko.pureComputed(() => {
    return '/dashboard/' + this.id() + '/';
  });

  dashboardMapUrl = ko.pureComputed(() => {
    return '/dashboard/' + this.id() + '/map/';
  });

  importFactsUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/import_observations/';
  });

  exportPlotsUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/export_plots/';
  });

  printPlotsUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/print_plots/';
  });

  fieldBookUrl = ko.pureComputed(() => {
    return '/trials/' + this.id() + '/field_book/';
  });

  gateInnovationLink = ko.pureComputed(() => {
    //TODO: Use environment variables
    //@ts-ignore
    const gateBaseUrl = {
      local: 'http://localhost:8081/',
      develop: '',
      staging: 'https://qa-sfsa-agserv-n553ishx4a-ew.a.run.app/',
      production: 'https://gate.sfsa-tools.org/',
    }[process.env.APP_ENV];
    return `${gateBaseUrl}innovations/${this.gateInnovationId()}/`;
  });

  status = ko.pureComputed(() => getTrialStateLabel(this.state()));

  ownerNames = ko.pureComputed(() => {
    return this.owners()
      .map((owner) => owner.name)
      .join(', ');
  });

  allowDisableSites = ko.pureComputed(() => {
    return this.trialType()?.allow_disable_sites ?? true;
  });

  static makeRandomizationSeed(): number {
    return Math.floor(Math.random() * 2147483647);
  }

  constructor(userData: UserData, data?: TrialData, template?: boolean) {
    if (APP_CONFIG.TRIAL_FIELDS_SPECIAL && data && data.created_from_template_id) {
      this.country = this.country.extend({ required: true });
      this.season = this.season.extend({ required: true });
    }

    if (userData) {
      this.owners([userData]);
    }

    if (data) {
      this.randomizationSeed = data.randomization_seed;
      this.editMode = data.edit_mode;

      this.id(data.id);
      this.nameJson(data.name_json);
      this.state(data.state as TrialState);
      this.archived(data.archived);
      this.glpFieldRestricted(!!data.glp_field_restricted);
      this.nameSlug(data.name_slug);
      this.plotNaming(data.plot_naming);
      this.rangeDirection(data.range_direction);
      this.plotDesign(data.plot_design);
      this.rcbType(data.rcb_type);
      this.createdAt(new Date(data.created));
      this.rowLength(data.row_length?.toString());
      this.rcbBlockWidth(data.rcb_block_width?.toString());
      this.rcbBlocksPerRow(data.rcb_blocks_per_row?.toString());
      this.separateTestSubjects(data.separate_test_subjects);
      this.rcbFirstBlockInTreatmentOrder(data.rcb_first_block_in_treatment_order);
      this.startingCorner(data.starting_corner);
      this.walkOrder(data.walk_order);
      this.trialType(data.trial_type);
      this.descriptionJson(data.description_json);
      this.plants(data.plants);
      this.spaceBetweenRows(data.space_between_rows);
      this.spaceInsideRows(data.space_inside_rows);
      this.squareMetersPerPlot(data.square_meters_per_plot);
      this.rowsPerPlot(data.rows_per_plot);
      this.borderJson(data.border_json);
      this.fieldSelectionJson(data.field_selection_json);
      this.plotLength(data.plot_length);
      this.plotWidth(data.plot_width);
      this.scheduledPlantingDate(parseDate(data.scheduled_planting_date));
      this.crop(data.crop);
      this.cultivationType(data.cultivation_type);
      this.fieldPreparationType(data.field_preparation_type);
      this.irrigationType(data.irrigation_type);
      this.country(data.country);
      this.region(data.region);
      this.agroRegion(data.agro_region);
      this.globalRegion(data.global_region);
      this.season(data.season);
      this.customers(data.customers);
      this.protocol(data.protocol);
      this.isCommercial(data.is_commercial);
      this.lastMeasurement(parseDateTime(data.last_timestamp));
      this.lastStaff = data.last_staff;
      this.sitePartnerNames = data.partners || [];
      this.collectedMeasurements(data.measurements_count ?? 0);
      this.measurementsToCollect(data.measurements_to_collect ?? 0);
      this.measurementsCalulationsAvailable(
        data.measurements_to_collect !== null || data.measurements_count !== null
      );
      this.dataCollectionStatus(data.data_collection_status ?? 'done');
      this.measurementsToCollectByToday(data.measurements_to_collect_by_today ?? 0);
      this.measurementsOptional(data.optional_measurements_count ?? 0);
      this.modified(parseDateTime(data.modified));
      this.assigned(data.assigned);
      this.isLegacyTrial = data.is_legacy_trial;
      this.enableBidirSync(data.enable_bidir_sync);
      this.disablePicturesFromGallery(data.disable_pictures_from_gallery);
      this.wasCreatedUsingTreatmentsFeature(data.was_created_using_treatments_feature);
      this.requiredLocationAccuracy(
        data.required_location_accuracy?.toString() ?? getLocationAccuracies()[0].value
      );
      this.warnLocationAccuracy(data.warn_location_accuracy);
      if (data.owners && data.owners.length > 0) {
        // old trials and data generated from a template may have not it set
        this.owners(data.owners);
      }
      this.tpp(data.tpp);
      this.scheduledVisits(data.scheduled_visits);
      this.traits(data.traits);
      this.sites(data.sites);
      if (data.tpp) {
        this.project(data.tpp.project);
      } else {
        this.project(data.project);
      }
      this.partner(data.partner);
      this.gateInnovationId(data.gate_innovation_id);
      this.gateInnovationName(data.gate_innovation_name);
      this.template = data.template;
      this.createdFromTemplateId = data.created_from_template_id;
      this.changeReasonPreference(data.change_reason_preference);
      this.defaultOverviewPresetId(data.default_overview_preset_id ?? null);
      this.cancellationReason(data.cancellation_reason);
      this.matchedFields(data.matched_fields || []);

      for (let obs of [this.rowLength, this.rcbBlockWidth, this.rcbBlocksPerRow]) {
        obs.isModified(false);
      }
    } else {
      // for new trials
      this.randomizationSeed = Trial.makeRandomizationSeed();
      this.template = template;
      this.wasCreatedUsingTreatmentsFeature(
        this.editMode === 'library' && session.tenant().treatment_management_enabled
      );
      this.changeReasonPreference(session.tenant().observation_history_enabled);
    }

    if (!this.template) {
      this.crop = this.crop.extend({ required: true });
    }

    this.canEditMMLibrary = canEditMMLibrary();

    this.autoNameTrials = APP_CONFIG.AUTO_NAME_TRIALS && !this.template;
    if (this.autoNameTrials) {
      let name = translate(this.nameJson());
      let prefix = this.namePrefix();
      if (data && name && name.indexOf(prefix) !== 0) {
        // this is an already existing trial with a name that doesn't match the current auto-naming algorithm,
        // let the user edit the name.
        this.autoNameTrials = false;
      } else {
        if (data && name) {
          this.nameSuffix(name.slice(prefix.length));
        }

        this.subscriptions.push(this.namePrefix.subscribe(this.updateNameSlug));
        this.subscriptions.push(this.nameSuffix.subscribe(this.updateNameSlug));
      }
    }
    if (!this.autoNameTrials) {
      this.nameJson = this.nameJson.extend({ i18nTextRequired: true });
      this.slugGenerator = new SlugGenerator(this.nameJson, null, this.nameSlug, {
        canEdit: this.isDraft(),
        fillIfEmpty: true,
      });
    }

    if (APP_CONFIG.TRIAL_FIELDS_SPECIAL && data && data.created_from_template_id) {
      this.country.isModified(true);
      this.season.isModified(true);
    }
  }

  dispose() {
    this.slugGenerator?.dispose();
    this.subscriptions.forEach((sub) => sub.dispose());
    this.subscriptions = [];
  }

  toData(): TrialData {
    let nameJson = this.nameJson();
    if (this.autoNameTrials) {
      nameJson = asI18nText(this.getAutoName());
    }

    return {
      id: this.id(),
      name_json: nameJson,
      state: this.state(),
      archived: this.archived(),
      name_slug: this.nameSlug(),
      plot_naming: this.plotNaming(),
      range_direction: this.rangeDirection(),
      plot_design: this.plotDesign(),
      rcb_type: this.rcbType(),
      row_length: emptyToNull(this.rowLength()),
      rcb_block_width: emptyToNull(this.rcbBlockWidth()),
      rcb_blocks_per_row: emptyToNull(this.rcbBlocksPerRow()),
      separate_test_subjects: this.separateTestSubjects(),
      rcb_first_block_in_treatment_order: this.rcbFirstBlockInTreatmentOrder(),
      starting_corner: this.startingCorner(),
      walk_order: this.walkOrder(),
      randomization_seed: this.randomizationSeed,
      trial_type: this.trialType(),
      description_json: this.descriptionJson(),
      plants: serializeNumber(this.plants()),
      space_between_rows: serializeNumber(this.spaceBetweenRows()),
      space_inside_rows: serializeNumber(this.spaceInsideRows()),
      square_meters_per_plot: serializeNumber(this.squareMetersPerPlot()),
      rows_per_plot: serializeNumber(this.rowsPerPlot()),
      border_json: this.borderJson(),
      field_selection_json: this.fieldSelectionJson(),
      plot_length: serializeNumber(this.plotLength()),
      plot_width: serializeNumber(this.plotWidth()),
      scheduled_planting_date: serializeDate(this.scheduledPlantingDate()),
      crop: this.crop(),
      cultivation_type: this.cultivationType(),
      field_preparation_type: this.fieldPreparationType(),
      irrigation_type: this.irrigationType(),
      country: this.country(),
      region: this.region(),
      agro_region: this.agroRegion(),
      global_region: this.globalRegion(),
      season: this.season(),
      customers: this.customers().slice(), // take copy for comparisons in wizard
      protocol: this.protocol(),
      is_commercial: this.isCommercial(),
      enable_bidir_sync: this.enableBidirSync(),
      disable_pictures_from_gallery: this.disablePicturesFromGallery(),
      required_location_accuracy: parseInt(this.requiredLocationAccuracy(), 10),
      was_created_using_treatments_feature: this.wasCreatedUsingTreatmentsFeature(),
      warn_location_accuracy: this.warnLocationAccuracy(),
      owners: this.owners().slice(), // take copy for comparisons in wizard
      tpp: this.tpp(),
      project: this.project(),
      partner: this.partner(),
      template: this.template,
      created_from_template_id: this.createdFromTemplateId,
      gate_innovation_id: this.gateInnovationId(),
      gate_innovation_name: this.gateInnovationName(),
      change_reason_preference: this.changeReasonPreference(),
      cancellation_reason: this.cancellationReason(),
      state_change_reason: this.stateChangeReason(),
    };
  }

  canActivate = ko.pureComputed(() => {
    return !this.template && this.state() !== TrialState.Active;
  });

  canSetCompleted = ko.pureComputed(() => {
    return (
      !this.template &&
      (this.isDraft() || this.state() === TrialState.Active || this.state() === TrialState.Paused)
    );
  });

  canSetDataValidated = ko.pureComputed(() => {
    return (
      !this.template &&
      (this.isDraft() ||
        this.state() === TrialState.Active ||
        this.state() === TrialState.Paused ||
        this.state() === TrialState.Completed)
    );
  });

  canArchive = ko.pureComputed(() => {
    return !this.archived();
  });

  canUnarchive = ko.pureComputed(() => {
    return this.archived();
  });

  isDraft = ko.pureComputed(() => {
    return (
      this.state() === TrialState.Draft ||
      this.state() === TrialState.Preview ||
      this.state() === TrialState.Approved
    );
  });

  isNotDraft = ko.pureComputed(() => {
    return !this.isDraft();
  });

  measurementCalculationIsAvailable = ko.pureComputed(() => {
    // NOTE: Please keep the two locations in sync.
    // https://github.com/3smobile/quicktrials-web/blob/develop/main/trials/tasks.py#L89
    if (
      this.archived() ||
      [TrialState.Draft, TrialState.Preview, TrialState.Approved].includes(this.state())
    ) {
      return true;
    }
    return this.measurementsCalulationsAvailable();
  });

  hasDefaultDesign() {
    return this.plotDesign() == 'default';
  }

  hasRCBDesign() {
    return this.plotDesign() == 'rcb';
  }

  hasLatinSquareDesign() {
    return this.plotDesign() == 'latin_square';
  }

  hasSplitPlotDesign() {
    return this.plotDesign() == 'split_plot';
  }

  hasCustomerDesign() {
    return this.plotDesign() == 'customer';
  }

  hasAlphaLatticeDesign() {
    return this.plotDesign() == 'alpha_lattice';
  }

  designSupportsMirroring() {
    return !this.hasCustomerDesign() && !this.hasManualDesign();
  }

  hasManualDesign() {
    return this.plotDesign() == 'manual';
  }

  customerNames(): string {
    return this.customers()
      .map((c) => c.name)
      .join(', ');
  }

  namePrefix = ko.pureComputed<string>(() => {
    let parts: string[] = [];
    if (this.country()) {
      parts.push(this.country().iso_country_code);
    }
    if (this.scheduledPlantingDate()) {
      parts.push(this.scheduledPlantingDate().getFullYear().toString());
    }
    if (this.season()) {
      parts.push(this.season().code || translate(this.season().name_json));
    }
    if (this.crop()) {
      parts.push(this.crop().anonymized_code || translate(this.crop().name_json));
    }
    if (this.trialType()) {
      parts.push(this.trialType().anonymized_code || translate(this.trialType().name_json));
    }
    for (let customer of this.customers()) {
      parts.push(customer.name);
    }

    return parts.join('_').toUpperCase();
  });

  private updateNameSlug = () => {
    if (this.isDraft()) {
      this.nameSlug(generateSlug(this.getAutoName()));
    }
  };

  private getAutoName(): string {
    return this.namePrefix() + this.nameSuffix();
  }
}

export class TrialWizardWarningController {
  warningMessages = ko.observableArray<WarningMessageType>([]);

  addMessage(type: WARNING_MESSAGES_TYPES, message: string) {
    this.removeMessage(type);
    this.warningMessages.push({ type, message });
  }

  removeMessage(type: WARNING_MESSAGES_TYPES) {
    this.warningMessages(
      this.warningMessages().filter((message: WarningMessageType) => message.type !== type)
    );
  }
}

interface TraitEditAction {
  forTrait: SelectedTrait;
  action: 'add' | 'remove';
  // The scheduled visit definitions live on the library trait, so the preview is asked for
  // that id while the visits are populated with the copy.
  previewMmId?: string;
}

export class TrialWizard {
  // if defined, and the trial hasn't been saved yet,
  // the server will copy charts/anova from this trial
  copyDashboardFromId: string = undefined;

  supportedResolutions = supportedResolutions;
  resolutionId = ko.observable(supportedResolutions[defaultResolutionIndex].id);
  warningController = ko.observable<TrialWizardWarningController>(new TrialWizardWarningController());

  editMode: EditMode;
  trial = ko.observable<Trial>();
  testSubjects = ko.observableArray<DatasetDimensionMeta>();
  treatments = ko.observableArray<Treatment>();
  // input applications of treatment trials (Applications wizard step)
  // TODO: (for another PR): is this really the right place for it? should it be inside ScheduledVisit instead?
  inputApplications = ko.observableArray<TrialInputApplication>();
  customPlotNumbers = ko.observable<boolean>(false);
  customPlotPosition = ko.observable<boolean>(false);
  excludeFromGenerationCombinationsOfControlAndNonControlTestSubjects = ko.observable<boolean>(false);
  changeReasonPreference = ko.observable<boolean>(false);
  savePlotGuides = false;
  plotGuides = ko.observable<PlotGuidesData>(null);
  hasImportedLayout = ko.observable<boolean>(false);
  // True only while applyCustomLayout writes the layout fields the user also
  // edits on the experimental-design step. The regenerate handlers there skip
  // the reset-imported-layout warning while it is set, so importing a layout
  // does not warn about resetting itself -- only a later user-initiated change
  // does.
  applyingImportedLayout = false;
  spMainDMId = ko.observable<string>().extend({
    required: true,
  });
  customerDimensions = ko.observableArray<DimensionData>();
  spRepBlocksPerRow = ko.observable<string>().extend({
    digit: true,
    max: MAX_INTEGER_VALUE,
  });
  manualAllowCreateDimension = ko.observable(false);
  alBlockSize = ko.observable('').extend({
    digit: true,
    min: 2,
    max: MAX_INTEGER_VALUE,
    required: { onlyIf: () => this.trial()?.plotDesign() === 'alpha_lattice' },
    validation: [
      {
        validator: (value: string) => {
          let size = parseInt(value, 10);
          if (isNaN(size) || size < 2) {
            return true;
          }

          return this.plotsPerReplication() % size === 0;
        },
        message: i18n.t([
          'number_of_plots_replication_error',
          'The number of plots in a replication must be divisible by the plots per block',
        ])(),
      },
      {
        validator: (value: string) => {
          let size = parseInt(value, 10);
          if (isNaN(size) || size < 2) {
            return true;
          }
          let replications = parseInt(this.replications().toString(), 10);
          if (isNaN(replications) || replications < 2 || replications > 4) {
            return true;
          }
          let nBlocks = this.plotsPerReplication() / size;

          if (replications === 3 && nBlocks % 2 === 0) {
            return size < nBlocks;
          } else {
            return size <= nBlocks;
          }
        },
        message: i18n.t([
          'number_of_plots_block_error',
          'The number of plots in a block must be less than the number of blocks in each replication',
        ])(),
      },
      {
        validator: (value: string) => {
          let size = parseInt(value, 10);
          if (isNaN(size) || size < 2) {
            return true;
          }
          let replications = parseInt(this.replications().toString(), 10);
          if (isNaN(replications) || replications !== 4) {
            return true;
          }
          let nBlocks = this.plotsPerReplication() / size;

          return nBlocks % 3 !== 0 && nBlocks % 2 !== 0;
        },
        message: i18n.t(
          "The number of blocks in a replication can't be a multiple of 2 or 3 when there are 4 replications"
        )(),
      },
    ],
  });
  alReplicationsPerRow = ko.observable('').extend({ digit: true, min: 1, max: MAX_INTEGER_VALUE });
  alBlocksPerReplicationRow = ko.observable('').extend({ digit: true, min: 1, max: MAX_INTEGER_VALUE });
  alPlotsPerBlockRow = ko.observable('').extend({ digit: true, min: 1, max: MAX_INTEGER_VALUE });
  sites = ko.observableArray<LimitToDimension>().extend({ required: true });
  canAddSites = ko.observable(false);
  allowTreatmentCreationOnClient = ko.observable(false);
  selectedTreatmentFactors = ko.observableArray<DimensionMeta>();
  replications = ko.observable<number>(1).extend({
    digit: true,
    serverError: true,
    validation: {
      validator: (value) => {
        if (!this.trial() || !this.trial().hasAlphaLatticeDesign()) {
          return true;
        }

        let replications = parseInt(value, 10);
        return !isNaN(replications) && replications >= 2 && replications <= 4;
      },
      message: i18n.t('Alpha lattices must have a number of replications between 2 and 4')(),
    },
  });
  scanCodeActions = ko.observableArray<TraitAction>();
  pictureAnalysisActions = ko.observableArray<TraitAction>();
  headerOrder = ko.observableArray<DataEntryHeaderData>();
  traitActions = ko.pureComputed(() => this.scanCodeActions().concat(this.pictureAnalysisActions()));
  traitActionsError = ko.observable<string>('');
  datasets = ko.observableArray<DatasetWizard>();
  scheduledVisits = ko.observableArray<ScheduledVisit>();
  forceRegeneratePlots = ko.observable<'full' | 'new_sites' | 'no'>('no');
  initialTemplate: boolean;
  template = ko.pureComputed(() => this.trial()?.template ?? this.initialTemplate);
  needToConfirmMissingTppTraits: boolean;
  tppTraitNameBySlug = new Map<string, string>();

  isDirty = ko.observable(false);

  errors: KnockoutValidationErrors;

  customerDimensionsSearch: FormSelectSearchConfiguration<DimensionData> = {
    getSummaryName: (entity) => entity.name_json,
    list: (params) => {
      const crop = this.trial().crop();
      const filters = { crop_ids: crop ? [crop.id] : undefined };

      return listForDMS(this.getCustomerDimensionDmIds(), filters, params);
    },
    entities: this.customerDimensions,
  };

  treatmentFactorsSearch: FormSelectSearchConfiguration<DimensionMeta> = {
    getSummaryName: (dimensionMeta) => dimensionMeta.nameJson(),
    list: () => {
      return Promise.resolve(
        this.testSubjectsWithDimensionMeta().map((testSubject) => testSubject.dimensionMeta())
      );
    },
    entities: this.selectedTreatmentFactors,
  };
  // The trait name is truncated with an ellipsis in the timeline box, so expose the full name for
  // the native tooltip.
  svTraitTitle = (svTrait: ScheduledVisitTraitData) => translate(svTrait.name);

  moveSVTraitUp = (svTrait: ScheduledVisitTraitData) => {
    this.moveSVTrait(svTrait, 'up');
  };

  moveSVTraitDown = (svTrait: ScheduledVisitTraitData) => {
    this.moveSVTrait(svTrait, 'down');
  };

  private moveSVTrait(svTrait: ScheduledVisitTraitData, direction: 'up' | 'down') {
    const dataset = this.datasets().find((ds) => ds.id() === svTrait.ds_id);
    const mm = dataset.measurementMetas().find((mm) => mm.id() === svTrait.mm_id);
    if (direction === 'up') {
      dataset.orderedMeasurementMetas.moveUp(mm);
    } else {
      dataset.orderedMeasurementMetas.moveDown(mm);
    }
    for (const sv of this.scheduledVisits()) {
      sv.onObservationsChange();
    }
  }

  applicationsForTreatment(treatment: Treatment): TrialInputApplication[] {
    return this.inputApplications().filter((application) => application.treatment === treatment);
  }

  // When the treatments are rebuilt as new objects (factor-combination
  // generation, imported layout), the applications' Treatment references go
  // stale and toData() would silently drop them from the payload — while they
  // stay visible in the applications table. Re-pair them by treatment order,
  // the same key the server uses to remap applications across re-derivations,
  // and drop only the applications whose order slot no longer exists.
  rebindApplicationsToTreatments() {
    const treatments = this.treatments();
    const treatmentsByOrder = new Map<number, Treatment>();
    for (const treatment of treatments) {
      treatmentsByOrder.set(treatment.order(), treatment);
    }

    this.inputApplications(
      this.inputApplications().filter((application) => {
        if (treatments.indexOf(application.treatment) >= 0) {
          return true;
        }
        const replacement = treatmentsByOrder.get(application.treatment.order());
        if (!replacement) {
          return false;
        }
        application.treatment = replacement;
        return true;
      })
    );
  }

  hasSites = (ds: ScheduledVisitObservationGrouping, mm: ScheduledVisitObservationGroupingItem): boolean => {
    if (this.editMode === 'manual' && ds instanceof DatasetWizard) {
      return ds.hasSites();
    }

    if (this.editMode === 'library') {
      return this.traits.selectedTraits().find((t) => t.id === mm.id())?.collectForSite ?? false;
    }

    // shouldn't happen
    console.warn('Warning: hasSites fallback');
    return false;
  };

  scheduledVisitsConfig: FormNestedEntitiesConfiguration<ScheduledVisit> = {
    title: '',
    addTitle: i18n.t('Schedule new visit')(),
    missingTitle: i18n.t('Not defined')(),
    entities: this.scheduledVisits,

    disableSupport: {
      canDisable: () =>
        [TrialState.Draft, TrialState.Preview, TrialState.Active].includes(this.trial().state()) &&
        canEditTrial(this.userData, this.trial()),
      disabled: (sv) => sv.disabled(),
      disable: (sv) => sv.disable(),
    },

    canRemove: (sv) => !sv.id || this.trial().isDraft(),
    add: () => {
      let sv = new ScheduledVisit(this.editMode, this.getScheduledVisitGrouping(), this.hasSites);
      this.scheduledVisits.push(sv);
      return sv;
    },
    remove: (sv) => {
      sv.dispose();
      this.scheduledVisits.remove(sv);
    },
    hasErrors: (sv) => sv.hasErrors(),
    showErrors: (sv) => sv.showErrors(),
    actions: [],
    getSummaryName: (sv) => sv.nameJson(),
    selectedEntity: ko.observable(null),
  };

  plotsModel: PlotsEditModel;

  private subscriptions: KnockoutSubscription[] = [];
  private tppControlSubscription: KnockoutSubscription | undefined;
  private dominantCvsControlSubscriptions = new Map<string, KnockoutSubscription>();

  constructor(
    public siteDM: DimensionMeta,
    public replicationDM: DimensionMeta,
    public userData: UserData,
    public traits: TrialTraits, // must be already loaded
    template: boolean,
    defaultEditMode: EditMode,
    data?: TrialWizardData
  ) {
    const editMode = data?.edit_mode ?? defaultEditMode;

    this.errors = ko.validation.group({
      ...this,
      datasets: editMode === 'manual' ? this.datasets : undefined,
    });

    this.initialTemplate = template;

    if (data) {
      this.resolutionId(getResolutionId(data));

      this.copyDashboardFromId = data.copy_dashboard_from_id;
      this.editMode = data.edit_mode;
      this.trial(new Trial(userData, data.trial));
      this.customPlotNumbers(data.custom_plot_numbers);
      this.customPlotPosition(data.custom_plot_position);
      this.plotGuides(data.plot_guides);
      if (!data.trial.id) {
        this.savePlotGuides = true;
      }
      this.spMainDMId(data.sp_main_dm_id);
      this.alBlockSize(data.al_block_size?.toString());
      this.alReplicationsPerRow(data.al_replications_per_row?.toString());
      this.alBlocksPerReplicationRow(data.al_blocks_per_replication_row?.toString());
      this.alPlotsPerBlockRow(data.al_plots_per_block_row?.toString());
      this.customerDimensions(data.customer_dimensions);
      this.sites(data.sites.map((d) => new LimitToDimension(null, siteDM, d)));
      this.canAddSites(data.can_add_sites);
      this.allowTreatmentCreationOnClient(data.allow_treatment_creation_on_client);
      this.spRepBlocksPerRow(data.sp_rep_blocks_per_row ? data.sp_rep_blocks_per_row.toString() : '');
      this.manualAllowCreateDimension(data.manual_allow_create_dimension);
      this.excludeFromGenerationCombinationsOfControlAndNonControlTestSubjects(
        data.exclude_from_generation_combinations_of_control_and_non_control_test_subjects
      );
      this.replications(data.replications);
      this.scanCodeActions(
        data.trait_actions
          .filter((a) => a.action_type === 'set_value' || a.action_type === 'play_sound')
          .map((a) => new TraitAction(a))
      );
      this.pictureAnalysisActions(
        data.trait_actions
          .filter(
            (a) => a.action_type === 'calculate_canopy_standard' || a.action_type === 'calculate_canopy_ai'
          )
          .map((a) => new TraitAction(a))
      );

      let seenSlugs: { [key: string]: boolean } = {};

      this.testSubjects(
        data.test_subjects.map((d) => {
          let slug = d.dimension_meta_id.slug;
          let isPredefined = false;

          if (DatasetDimensionMeta.predefinedSlugs.indexOf(slug) >= 0 && !seenSlugs[slug]) {
            seenSlugs[slug] = true;
            isPredefined = true;
          }

          return new DatasetDimensionMeta(this, d, isPredefined);
        })
      );
      if (data.allow_treatment_creation_on_client) {
        this.selectedTreatmentFactors(
          this.testSubjectsWithDimensionMeta()
            .filter((testSubject) => testSubject.allowCreate())
            .map((testSubject) => testSubject.dimensionMeta())
        );
      }

      this.treatments(data.treatments.map((treatmentData) => new Treatment(treatmentData)));

      if (editMode === 'library') {
        this.scheduledVisits(
          data.scheduled_visits.map(
            (data) =>
              new ScheduledVisit(this.editMode, this.getScheduledVisitGrouping(), this.hasSites, data, {
                templateId: data.template_id,
              })
          )
        );
      } else {
        // must be initialized last, because it depends on the trial wizard data
        this.datasets(data.datasets.map((d) => new DatasetWizard(this, d)));
        this.scheduledVisits(
          data.scheduled_visits.map(
            (data) => new ScheduledVisit(this.editMode, this.datasets, this.hasSites, data)
          )
        );
      }

      this.inputApplications(
        (data.input_applications ?? []).flatMap((applicationData) => {
          // scheduled_visit_index and treatment_index are set when copying from a template
          const scheduledVisit =
            applicationData.scheduled_visit_index != null
              ? this.scheduledVisits()[applicationData.scheduled_visit_index]
              : this.scheduledVisits().find(
                  (candidate) => candidate.id === String(applicationData.scheduled_visit_id)
                );
          const treatment =
            applicationData.treatment_index != null
              ? this.treatments()[applicationData.treatment_index]
              : this.treatments().find((candidate) => candidate.id() === applicationData.treatment_id);

          if (!treatment || !scheduledVisit) {
            return [];
          }

          return [new TrialInputApplication(scheduledVisit, treatment, applicationData)];
        })
      );

      this.headerOrder(data.available_headers ?? []);

      for (let obs of [
        this.alBlockSize,
        this.alReplicationsPerRow,
        this.alBlocksPerReplicationRow,
        this.alPlotsPerBlockRow,
      ]) {
        obs.isModified(false);
      }
      this.updateTreatmentFactorsAllowCreate();
    } else {
      this.editMode = editMode;
      this.trial(new Trial(userData, undefined, template));
    }
    this.trial().editMode = this.editMode;
    // preselect split plot main subject
    this.subscriptions.push(this.testSubjects.subscribe(this.updateSPMainDim));

    this.subscriptions.push(
      this.selectedTreatmentFactors.subscribe(() => {
        this.updateTreatmentFactorsAllowCreate();
      })
    );
    this.subscriptions.push(
      this.allowTreatmentCreationOnClient.subscribe(() => {
        this.updateTreatmentFactorsAllowCreate();
      })
    );
    this.subscriptions.push(
      this.trial().tpp.subscribe(async () => {
        this.checkTrialTPP(this.testSubjects(), this.trial().tpp());

        if (this.trial().tpp() != null && this.testSubjects().length === 0 && this.trial().isDraft()) {
          await this.addCropTestSubject();
        }
        const cropVarietyTestSubject = this.getCropVarietyTestSubject();

        this.addTppDominantVarieties(cropVarietyTestSubject?.limitTo);
      })
    );

    this.subscriptions.push(
      this.testSubjects.subscribe(() => {
        this.checkTrialTPP(this.testSubjects(), this.trial().tpp());
      })
    );
    this.subscriptions.push(
      this.scheduledVisits.subscribe(() => {
        if (this.scheduledVisits().length > 0) {
          const periodicVisits = this.scheduledVisits().filter((visit) => !!visit.repetitionNumberOfDays);
          if (periodicVisits.length > 0 && this.template()) {
            this.warningController().addMessage(
              WARNING_MESSAGES_TYPES.PERIODIC_VISIT,
              i18n.t(
                'Periodic visits do not appear in trial templates. They will be generated in trials from this template'
              )()
            );
          } else {
            this.warningController().removeMessage(WARNING_MESSAGES_TYPES.PERIODIC_VISIT);
          }
        }
      })
    );

    this.subscriptions.push(
      this.traits.selectedTraits.subscribe(async () => {
        if (this.traits.selectedTraits().length > 0) {
          const dependenciesPromises = chunk(this.traits.selectedTraits(), 100).map((toAddChunk) =>
            listDependencies({
              for_ids: toAddChunk.map((mm) => mm.mmId),
              crop_id: this.trial().crop()?.id,
            })
          );
          const dependenciesChunks = await Promise.all(dependenciesPromises);

          let invalid: datasetsApi.MeasurementMetaData[] = [];
          for (const dependenciesChunk of dependenciesChunks) {
            invalid.concat(...dependenciesChunk.invalid);
          }

          this.showTraitsWarningMessage(invalid);
        }
        this.traits.loaded() && this.checkTPPTraits();
      })
    );

    this.subscriptions.push(
      this.trial().trialType.subscribe(() => {
        this.checkTPPTraits();
      })
    );

    this.subscriptions.push(
      this.testSubjects.subscribe(() => {
        const cropVarietyTestSubject = this.getCropVarietyTestSubject();
        if (cropVarietyTestSubject != undefined) {
          if (this.tppControlSubscription == undefined) {
            this.tppControlSubscription = cropVarietyTestSubject.limitTo.subscribe(() =>
              this.checkTppControlVarieties()
            );
            this.subscriptions.push(this.tppControlSubscription);
          }
        } else {
          this.tppControlSubscription?.dispose();
        }
      })
    );

    const cropVarietyTestSubject = this.getCropVarietyTestSubject();
    if (cropVarietyTestSubject) {
      this.tppControlSubscription = cropVarietyTestSubject.limitTo.subscribe(() =>
        this.checkTppControlVarieties()
      );
      this.subscriptions.push(this.tppControlSubscription);
      this.checkTppControlVarieties();
    }

    this.updateSPMainDim(this.testSubjects());

    this.setPlots(data?.plots ?? datasetsApi.EMPTY_PLOT_LIST_DATA, !data?.trial.id);

    // A manual layout (imported or hand-placed) is defined by its saved plots;
    // changing the replications or design on the experimental-design step
    // regenerates and discards it. hasImportedLayout drives the warning there,
    // but it is client-only and would otherwise be lost on the save+reload that
    // step navigation triggers, so re-derive it from the loaded layout.
    const loadedPlotCount = data?.plots?.plots?.id?.length ?? 0;
    if (this.trial().plotDesign() === 'manual' && loadedPlotCount > 0) {
      this.hasImportedLayout(true);
    }
  }

  libraryScheduledVisitGrouping: KnockoutObservableArray<ScheduledVisitObservationGrouping> =
    ko.observableArray([
      {
        id: ko.observable(''),
        nameJson: ko.observable(asI18nText('')),
        order: ko.observable(0),
        properMeasurementMetas: () => {
          return this.traits.selectedTraits().map((t, index) => ({
            id: ko.observable(t.mmId),
            copiedFromId: undefined as string | undefined, // not present for library trials, only for manual ones
            libraryTemplateId: t.referenceId,
            nameJson: ko.observable(t.name),
            // for selectedTraits(), the order is always the index (see TrialTraits.toData)
            order: ko.observable(index),
            type: ko.observable(t.type),
          }));
        },
      },
    ]);

  private getScheduledVisitGrouping(): KnockoutObservableArray<ScheduledVisitObservationGrouping> {
    if (this.editMode === 'library') {
      return this.libraryScheduledVisitGrouping;
    }

    return this.datasets;
  }

  private showTraitsWarningMessage = (traits: datasetsApi.MeasurementMetaData[]) => {
    this.warningController().removeMessage(WARNING_MESSAGES_TYPES.TRAITS_WRONG_CROP);
    if (traits.length > 0) {
      const notAddedNames = `: ${traits.map((mm) => translate(mm.name_json)).join(',')}`;
      const cropName = ` "${translate(this.trial().crop().name_json)}"`;
      const message = i18n.t("The following traits weren't added")();
      const reason = i18n.t('They are not assigned to crop')();
      const fullMessage = message + notAddedNames + '. ' + reason + cropName;

      this.warningController().addMessage(WARNING_MESSAGES_TYPES.TRAITS_WRONG_CROP, fullMessage);
    }
  };

  checkTPPTraits = async () => {
    this.warningController().removeMessage(WARNING_MESSAGES_TYPES.TPP_TRAITS_NOT_IN_TRIAL);
    if (this.trial().tpp()) {
      const missingTppTraitNames: string[] = this.getMissingTraitsNamesFromTpp();
      if (missingTppTraitNames.length > 0) {
        const message = `${i18n.t(
          'You do not have all required traits of the attached TPP in your trial:'
        )()} ${missingTppTraitNames.join(', ')}`;
        this.warningController().addMessage(WARNING_MESSAGES_TYPES.TPP_TRAITS_NOT_IN_TRIAL, message);
      }
    }
  };

  private addDominantCvControlCheck(dominantCvLimitTo: LimitToDimension) {
    const id = dominantCvLimitTo.id();
    if (!this.dominantCvsControlSubscriptions.has(id)) {
      this.dominantCvsControlSubscriptions.set(
        id,
        dominantCvLimitTo.control.subscribe(() => this.checkTppControlVarieties())
      );
    }
  }

  private getCropVarietyTestSubject() {
    return this.testSubjects().find((ts) => ts.useForPlot() && ts.dimensionMeta()?.slug() == 'crop_variety');
  }

  getMissingTraitsNamesFromTpp = (): string[] => {
    let selectedTraitSlugs = new Set<string>();
    if (this.trial().editMode === 'manual') {
      this.datasets().forEach((dataset) => {
        dataset.measurementMetas().forEach((measurementMeta) => {
          selectedTraitSlugs.add(measurementMeta.nameSlug());
        });
      });
    } else {
      selectedTraitSlugs = new Set(this.traits.selectedTraits().map((trait) => trait.nameSlug));
    }
    return this.trial()
      .tpp()
      .traits.filter((trait) => {
        if (!trait.trial_types?.length || !this.trial().trialType()) {
          return true;
        }
        return trait.trial_types
          .map((traitTrialType) => traitTrialType.id)
          .includes(this.trial().trialType()?.id);
      })
      .map((trait) => trait.measurement_meta)
      .filter((trait) => trait && !selectedTraitSlugs.has(trait.name_slug))
      .map((trait) => translate(trait.name_json));
  };

  private async addCropTestSubject() {
    let cvTestSubject = new DatasetDimensionMeta(this);
    cvTestSubject.useForPlot(true);
    cvTestSubject.dimensionMeta(new DimensionMeta(await dimensionMetasApi.retrieve('crop_variety')));
    cvTestSubject.isPredefined(true);
    cvTestSubject.order(0);
    this.testSubjects.push(cvTestSubject);
  }

  private addTppDominantVarieties(limitTo: ko.ObservableArray<LimitToDimension>) {
    if (limitTo == undefined || !this.trial().isDraft() || this.trial().tpp() == null) return;
    for (const dominantCv of this.trial().tpp().dominant_varieties) {
      let dominantCvLimitTo = limitTo().find((lt) => lt.id() === dominantCv.id);
      if (dominantCvLimitTo == undefined) {
        dominantCvLimitTo = new LimitToDimension(
          this,
          this.getCropVarietyTestSubject().dimensionMeta(),
          dominantCv
        );
        dominantCvLimitTo.control(true);
        limitTo.push(dominantCvLimitTo);
      }
      this.addDominantCvControlCheck(dominantCvLimitTo);
    }
    const benchmarkVarieties = this.trial()
      .tpp()
      .traits.map((trait) => trait.benchmark_varieties.map((variety) => variety))
      .reduce((a, b) => a.concat(b), []);

    for (const benchmarkVariety of benchmarkVarieties) {
      let benchmarkVarietyLimitTo = limitTo().find((lt) => lt.id() === benchmarkVariety.id);
      if (benchmarkVarietyLimitTo == undefined) {
        benchmarkVarietyLimitTo = new LimitToDimension(
          this,
          this.getCropVarietyTestSubject().dimensionMeta(),
          benchmarkVariety
        );
        limitTo.push(benchmarkVarietyLimitTo);
      }
    }
  }

  private checkTppControlVarieties() {
    if (this.trial().tpp() == null) return;
    const limitTo = this.getCropVarietyTestSubject()?.limitTo();
    if (limitTo == undefined) return;
    const tppDominantVarieties = this.trial().tpp().dominant_varieties;

    let missingDominantCvs = [];
    let dominantCvsWithoutControl = [];
    for (const dominantCv of tppDominantVarieties) {
      const dominantCvLimitTo = limitTo.find((lt) => lt.id() === dominantCv.id);
      if (dominantCvLimitTo == undefined) {
        missingDominantCvs.push(translate(dominantCv.name_json));
      } else if (!dominantCvLimitTo.control()) {
        dominantCvsWithoutControl.push(translate(dominantCv.name_json));
      }
      dominantCvLimitTo && this.addDominantCvControlCheck(dominantCvLimitTo);
    }

    for (let [key, subscription] of Array.from(this.dominantCvsControlSubscriptions.entries())) {
      if (limitTo.findIndex((lt) => lt.id() === key) == -1) {
        subscription.dispose();
        this.dominantCvsControlSubscriptions.delete(key);
      }
    }

    this.warningController().removeMessage(WARNING_MESSAGES_TYPES.DOMINANT_VARIETY_NO_CONTROL_SET);
    if (missingDominantCvs.length > 0) {
      this.warningController().addMessage(
        WARNING_MESSAGES_TYPES.DOMINANT_VARIETY_NOT_SELECTED,
        `${i18n.t([
          'dominant_variety_not_selected',
          'A current market dominant variety is not selected: ',
        ])()} ${missingDominantCvs.join(', ')}`
      );
    }
    this.warningController().removeMessage(WARNING_MESSAGES_TYPES.DOMINANT_VARIETY_NOT_SELECTED);
    if (dominantCvsWithoutControl.length > 0) {
      this.warningController().addMessage(
        WARNING_MESSAGES_TYPES.DOMINANT_VARIETY_NO_CONTROL_SET,
        `${i18n.t([
          'dominant_variety_no_control',
          'A current market dominant variety is selected, but "control" flag is not set: ',
        ])()} ${dominantCvsWithoutControl.join(', ')}`
      );
    }
  }

  private checkTrialTPP(testSubjects: DatasetDimensionMeta[], tpp: TPPListData) {
    const slugs = testSubjects.map((ts) => {
      if (ts.useForPlot()) return ts.dimensionMeta()?.slug();
    });
    this.warningController().removeMessage(WARNING_MESSAGES_TYPES.TPP_WRONG_TEST_SUBJECT);
    if (slugs.indexOf('crop_variety') < 0 && tpp) {
      this.warningController().addMessage(
        WARNING_MESSAGES_TYPES.TPP_WRONG_TEST_SUBJECT,
        SERVER_INFO.USE_FACTORS_NAMING || session.tenant().treatment_templates_enabled
          ? i18n.t("The treatment factor has to be 'Crop Variety' to be added to a TPP")()
          : i18n.t("The test subject has to be 'Crop Variety' to be added to a TPP")()
      );
    }
  }

  setPlots(plots: datasetsApi.PlotListData, isNew: boolean) {
    this.plotsModel = new PlotsEditModel(plots, this, isNew);
  }

  async loadLibrarySV(): Promise<void> {
    if (this.editMode !== 'library' || this.traits.needsLoadLibrarySV.length === 0) {
      return;
    }

    // need to reload this on each trait add/remove
    // load before updating the scheduled visits: they need the traits to exist to add them to svs
    await this.traits.load();

    for (const action of this.traits.needsLoadLibrarySV) {
      await this._loadLibrarySV(action);
    }

    if (this.traits.needsLoadLibrarySV.find((x) => x.action === 'add')) {
      // the base date might appear later, when the user adds it, or it might already be there
      // when the sv that needs it is added:
      // to cover all cases, we set it wherever possible after a new trait is added.
      await this._setupBaseDates();
    }

    this.traits.needsLoadLibrarySV = [];
  }

  private async _setupBaseDates() {
    const dates = await listTrialScheduledVisitsTemplateDates(
      asInt(this.trial().id()),
      this.scheduledVisits()
        .map((sv) => asInt(sv.id) || sv.templateId)
        .filter((id) => !!id)
    );
    const datesMap = new Map(dates.map((row) => [row.library_scheduled_visit_id, row]));

    for (const sv of this.scheduledVisits()) {
      const data = datesMap.get(sv.templateId);
      if (!data || sv.baseDate()) {
        continue;
      }

      const candidates = sv.selectableObservations(
        { limit: Number.MAX_SAFE_INTEGER },
        (_dsIdx, mm) => mm.type() === 'date'
      );
      const match = candidates.find((t) => t.mm.libraryTemplateId === data.trait_reference_id);
      if (match) {
        sv.baseDate(match);
        sv.days(data.days_offset?.toString() ?? '');
        sv.daysAfterPlanting(data.days_after_planting?.toString() || '');
      }
    }
  }

  async _loadLibrarySV({ forTrait, action, previewMmId }: TraitEditAction): Promise<void> {
    // this is add-only, and should run only when the user explicitly adds or removes a trait and nothing more

    if (action === 'add') {
      const existingScheduledVisits = new Map(this.scheduledVisits().map((sv) => [sv.code(), sv]));

      const serverPreviews = await listTrialScheduledVisitsPreviews(this.trial().id(), [
        {
          mm_id: previewMmId ?? forTrait.mmId,
          mm_template_id: forTrait.templateId,
          order: 0, // we're passing only 1 trait, so order doesn't really matter
          disabled: forTrait.disabled(),
        },
      ]);
      const previews = serverPreviews.flatMap((preview) =>
        expandWithPeriodicVisits(preview, this.template())
      );

      for (const preview of previews) {
        let sv = existingScheduledVisits.get(preview.code);

        let needAddSv = false;
        if (!sv) {
          // preview.id is a library id only when preview.is_new === true
          const libraryTemplateId =
            preview.template_id ?? (preview.is_new ? parseInt(preview.id, 10) : undefined);

          sv = new ScheduledVisit(
            this.editMode,
            this.getScheduledVisitGrouping(),
            this.hasSites,
            {
              id: null,
              name_json: preview.name_json,
              code: preview.code,
              n_min_visits: preview.n_min_visits,
              n_max_visits: preview.n_max_visits,
              repetition_number_of_days: preview.repetition_number_of_days,
              maximum_number_of_repetitions: preview.maximum_number_of_repetitions,
              days_offset: preview.days_offset ?? 0,
              days_after_planting: preview.days_after_planting ?? 0,
              order: 0, // this isn't used by the ScheduledVisit constructor, any value will do
              mobile_date_popup_enabled: preview.mobile_date_popup_enabled,
              traits: preview.observations.map((o) => ({ name_json: o.name })),
              observations: [],
              disabled: false,
            },
            {
              templateId: preview.is_periodic_repetition ? undefined : libraryTemplateId || undefined,
            }
          );
          needAddSv = true;
        }

        const candidates = sv.selectableObservations({ limit: Number.MAX_SAFE_INTEGER }, () => true);
        for (const previewObservation of preview.observations) {
          // a library visit not yet in the trial also reports the trial traits it observes, so
          // only the one observation that came back under the id we asked for is forTrait
          const observationMmId =
            previewMmId && previewObservation.mm_id === previewMmId
              ? forTrait.mmId
              : previewObservation.mm_id;
          const toAdd = candidates.find((c) => c.mm.id() === observationMmId);
          // toAdd can be empty only if the user queued up both an add and a remove operation together
          // toAdd may already be present (e.g. after editing a trait, it gets re-added), don't add it twice
          if (
            toAdd &&
            !sv.observations().find((obs) => obs.mm.libraryTemplateId === toAdd.mm.libraryTemplateId)
          ) {
            sv.observations.push(toAdd);
          }
        }

        if (needAddSv) {
          if (sv.observations().length === 0) {
            // sv was created because the user queued up both an add and a remove: just don't add it, it's going to be empty anyway
            sv.dispose();
          } else {
            existingScheduledVisits.set(sv.code(), sv);
            this.scheduledVisits.push(sv);
          }
        }
      }
    } else {
      for (const sv of this.scheduledVisits().slice()) {
        const idx = sv.observations().findIndex((obs) => obs.mm.libraryTemplateId === forTrait.referenceId);
        if (idx >= 0) {
          sv.observations.splice(idx, 1);

          // Automatically remove a scheduled visit only in a very specific scenario:
          // - it wasn't saved yet (this.scheduledVisitsConfig.canRemove will also automatically be true)
          // - its last observation was removed just now (i.e. we don't just delete any scheduled visit that happens to be empty)
          // - it has no input applications: this is implicitly guaranteed by the empty id check
          if (!sv.id && sv.observations().length === 0) {
            this.scheduledVisitsConfig.remove(sv);
          }
        }
      }
    }
  }

  uploading = ko.pureComputed(() => {
    for (let ds of this.datasets()) {
      for (let mm of ds.measurementMetas()) {
        for (let pic of mm.pictures()) {
          if (pic.imageUpload.uploading()) {
            return true;
          }
        }
      }
    }

    return false;
  });

  getValidationTarget(): {} {
    let target = ko.utils.extend({}, this);

    if (!this.trial().hasSplitPlotDesign()) {
      delete (<any>target)['spMainDMId'];
    }
    delete target['plotsModel'];
    delete target['scheduledVisitsConfig'];
    delete target['subscriptions'];
    (<any>target).datasets = this.datasets().map((dataset) => dataset.getValidationTarget());

    return target;
  }

  dispose() {
    this.subscriptions.forEach((sub) => sub.dispose());
    this.dominantCvsControlSubscriptions.forEach((sub) => sub.dispose());
    for (let dataset of this.datasets()) {
      dataset.dipose();
    }
    for (let sv of this.scheduledVisits()) {
      sv.dispose();
    }
    if (this.trial()) {
      this.trial().dispose();
    }
  }

  private updateSPMainDim = (ddms: DatasetDimensionMeta[]) => {
    if (
      ddms.length > 0 &&
      (!this.spMainDMId() ||
        indexOf(ddms, (ddm) => ddm.dimensionMeta() && ddm.dimensionMeta().id() === this.spMainDMId()) === -1)
    ) {
      if (ddms.length > 0 && ddms[0].dimensionMeta()) {
        this.spMainDMId(ddms[0].dimensionMeta().id());
      }
    }
  };

  private updateTreatmentFactorsAllowCreate = () => {
    this.testSubjects().forEach((testSubject) => {
      const isSelectedFactor = this.selectedTreatmentFactors().some(
        (selectedFactor) => selectedFactor.id() === testSubject.dimensionMeta()?.id()
      );

      testSubject.allowCreate(this.allowTreatmentCreationOnClient() && isSelectedFactor);
    });
  };

  canSafelyRegeneratePlots() {
    return (
      this.forceRegeneratePlots() !== 'no' ||
      (!this.customPlotNumbers() && !this.customPlotPosition() && this.trial().isDraft())
    );
  }

  // Mirrors the server's check: it rejects generate_plots for trials that don't
  // accept edits (e.g. an approved template), but never for an unsaved trial.
  canGeneratePlots() {
    const trial = this.trial();
    return !trial || !trial.id() || canEditTrial(this.userData, trial);
  }

  testSubjectsWithDimensionMeta(): DatasetDimensionMeta[] {
    return this.testSubjects().filter((s) => s.dimensionMeta() && s.dimensionMeta().id());
  }

  masterDataset(): DatasetWizard {
    for (let dataset of this.datasets()) {
      if (!dataset.hasExcludedPlotDM()) {
        return dataset;
      }
    }

    return null;
  }

  removeDataset(dataset: DatasetWizard) {
    this.datasets.remove(dataset);
    for (let sv of this.scheduledVisits()) {
      sv.onDatasetRemoved(dataset);
    }
  }

  removeMeasurementMeta(dataset: DatasetWizard, mm: MeasurementMeta) {
    dataset.orderedMeasurementMetas.remove(mm);
    for (let sv of this.scheduledVisits()) {
      sv.onMeasurementMetaRemoved(mm);
    }
  }

  toData(): TrialWizardData {
    let masterDataset = this.masterDataset();
    const isLibrary = this.editMode === 'library';

    return {
      edit_mode: this.editMode,
      trial: this.trial().toData(),
      test_subjects: this.testSubjects().map((x) => x.toData(true)),
      // Do not send treatments to the server until the functionality of saving is implemented
      treatments: this.treatments().map((treatment) => treatment.toData()),
      custom_plot_numbers: this.customPlotNumbers(),
      custom_plot_position: this.customPlotPosition(),
      sp_main_dm_id: this.spMainDMId(),
      al_block_size: emptyToNull(this.alBlockSize()),
      al_replications_per_row: emptyToNull(this.alReplicationsPerRow()),
      al_blocks_per_replication_row: emptyToNull(this.alBlocksPerReplicationRow()),
      al_plots_per_block_row: emptyToNull(this.alPlotsPerBlockRow()),
      customer_dimensions: this.customerDimensions().slice(), // copy for changed detection
      sp_rep_blocks_per_row: serializeNumber(this.spRepBlocksPerRow()),
      manual_allow_create_dimension: this.manualAllowCreateDimension(),
      sites: this.sites().map((x) => this.siteToData(x)),
      can_add_sites: this.canAddSites(),
      allow_treatment_creation_on_client: this.allowTreatmentCreationOnClient(),
      replications: this.replications(),
      save_plot_guides: this.savePlotGuides,
      plot_guides: this.savePlotGuides ? this.plotGuides() : null,
      save_plots: this.plotsModel.needsSave,
      plots: this.plotsModel.needsSave ? this.plotsModel.toData() : this.plotsModel.emptyData(),
      // Overridden in TrialWizard.saveRequest before sending: true unless this is a pure
      // state/metadata change on an existing library trial. Kept constant here so it
      // doesn't interfere with the toData()-based change detection (lastSavedData comparisons).
      need_rederive: true,
      datasets: this.datasets().map((x) => x.toData(x === masterDataset)),
      scheduled_visits: this.scheduledVisits().map((sv, idx) => sv.toData(idx)),
      traits: isLibrary ? this.traits.toData() : [],
      copy_dashboard_from_id: this.copyDashboardFromId,
      was_created_using_treatments_feature: this.trial().wasCreatedUsingTreatmentsFeature(),
      exclude_from_generation_combinations_of_control_and_non_control_test_subjects:
        this.excludeFromGenerationCombinationsOfControlAndNonControlTestSubjects(),
      ...toResolutionData(this.resolutionId()),
      trait_actions: this.traitActions().map((traitAction) => traitAction.toData()),
      input_applications:
        session.isInputApplicationsEnabled() && this.trial().wasCreatedUsingTreatmentsFeature()
          ? this.inputApplications().flatMap((application) => {
              const scheduledVisitIndex = this.scheduledVisits().indexOf(application.scheduledVisit);
              const treatmentIndex = this.treatments().indexOf(application.treatment);

              if (scheduledVisitIndex < 0 || treatmentIndex < 0) {
                // the scheduled visit / treatment was removed, so the application is dropped
                return [];
              }

              return [application.toData(scheduledVisitIndex, treatmentIndex)];
            })
          : undefined,
      available_headers: this.headerOrder().slice(), // read-only on the server; copy for change detection
      default_header_order: this.headerOrder().map((header) => header.id),
      is_dirty: this.isDirty(),
    };
  }

  private siteToData(site: LimitToDimension) {
    let data = site.toData();
    if (!(this.trial()?.allowDisableSites() ?? false)) {
      data.disabled = false;
    }

    return data;
  }

  initialPlotSites() {
    return this.sites().map((site) => {
      return {
        site_id: site.id(),
        n_columns: null as null,
        n_ranges: null as null,
      };
    });
  }

  previewPlotsRequest(): Promise<datasetsApi.PlotGenerationData> {
    // Use only the first site for preview
    const siteIds = this.sites().length > 0 ? [this.sites()[0].id()] : [];
    return this._generatePlotsRequest(siteIds, null, false, false, this.plotGuides());
  }

  generateFreshPlotsRequest(): Promise<datasetsApi.PlotGenerationData> {
    return this._generatePlotsRequest(
      this.sites().map((site) => site.id()),
      null,
      false,
      false,
      this.plotGuides()
    );
  }

  generatePlotsRequest(options: {
    fullReset: boolean;
    useCustom: boolean;
  }): Promise<datasetsApi.PlotGenerationData> {
    let preserveExisting = this.forceRegeneratePlots() === 'new_sites';
    let plotData = this.plotsModel.toData();
    if (options.fullReset) {
      plotData.plots.excluded = plotData.plots.excluded.map((_) => false);
      plotData.plots.custom_name = plotData.plots.custom_name.map((_) => false);
      plotData.plots.external_id = plotData.plots.external_id.map((_) => '');
    }
    return this._generatePlotsRequest(
      this.sites().map((site) => site.id()),
      plotData,
      preserveExisting,
      options.useCustom,
      this.plotGuides()
    );
  }

  private getTreatmentsForPlotGeneration(): Treatment[] {
    if (this.trial()?.isDraft()) {
      return this.treatments().filter((treatment) => !treatment.disabled());
    }

    // For non-draft trials we must still include disabled treatments
    // that are used in plots so that these plots don't get removed.
    const treatmentIdsWithPlots = new Set(
      this.plotsModel.toData().plots.treatment_id.filter((id) => id != null)
    );
    return this.treatments().filter(
      (treatment) => !treatment.disabled() || treatmentIdsWithPlots.has(treatment.id()!)
    );
  }

  private async _generatePlotsRequest(
    sites: string[],
    existing: datasetsApi.PlotListData,
    preserveExisting: boolean,
    useCustom: boolean,
    plotGuides: PlotGuidesData
  ): Promise<datasetsApi.PlotGenerationData> {
    let trial = this.trial();
    let ddms = this.testSubjectsWithDimensionMeta();
    let emptyResult: datasetsApi.PlotGenerationData = {
      isValid: true,
      entityId: null,
      errors: {},
      custom_plot_position: false,
      custom_plot_numbers: false,
      plots: null,
      separate_timeout: false,
    };

    // If the plot design is Randomized Complete Block (RCB) and replications count is greater than 18,
    // we must disable the separate test subjects option and inform the user about it.
    // The reason for this is bad performance of our "separate test subjects" algorithm for larger datasets.
    if (trial.plotDesign() == 'rcb' && trial.separateTestSubjects() && this.replications() > 18) {
      this.trial().separateTestSubjects(false);

      infoDialog(
        i18n.t('Warning')(),
        i18n.t(
          'The "Keep test subjects separated" option has been disabled because the RCB design with more than 18 replications is not supported.'
        )()
      );
    }

    let requestData: datasetsApi.PlotGenerationRequestData = {
      trial_id: trial.id(),
      preserve_existing: preserveExisting,
      plot_naming: trial.plotNaming(),
      range_direction: trial.rangeDirection(),
      plot_design: trial.plotDesign(),
      rcb_type: trial.rcbType(),
      row_length: emptyToNull(trial.rowLength()),
      rcb_block_width: emptyToNull(trial.rcbBlockWidth()),
      rcb_blocks_per_row: emptyToNull(trial.rcbBlocksPerRow()),
      al_block_size: emptyToNull(this.alBlockSize()),
      al_replications_per_row: emptyToNull(this.alReplicationsPerRow()),
      al_blocks_per_replication_row: emptyToNull(this.alBlocksPerReplicationRow()),
      al_plots_per_block_row: emptyToNull(this.alPlotsPerBlockRow()),
      separate_test_subjects: trial.separateTestSubjects(),
      rcb_first_block_in_treatment_order: trial.rcbFirstBlockInTreatmentOrder(),
      starting_corner: trial.startingCorner(),
      walk_order: trial.walkOrder(),
      randomization_seed: trial.randomizationSeed,
      crop_id: trial.crop() ? trial.crop().id : null,
      custom_plot_numbers: useCustom && this.customPlotNumbers(),
      custom_plot_position: useCustom && this.customPlotPosition(),
      rcb_replications: this.replications(),
      sp_main_dm_id: null,
      sp_rep_blocks_per_row: null,
      treatments: this.getTreatmentsForPlotGeneration().map((treatment) => treatment.toData()),
      plot_guides: plotGuides,
      existing_plots: existing,
      dataset_dimension_metas: ddms.map((ddm) => ddm.toData(true)),
      sites,
      exclude_from_generation_combinations_of_control_and_non_control_test_subjects:
        this.excludeFromGenerationCombinationsOfControlAndNonControlTestSubjects(),
      was_created_using_treatments_feature: trial.wasCreatedUsingTreatmentsFeature(),
    };

    if (trial.hasSplitPlotDesign()) {
      if (!this.spMainDMId()) {
        return Promise.resolve(emptyResult);
      }

      requestData.sp_main_dm_id = this.spMainDMId();
      requestData.sp_rep_blocks_per_row = serializeNumber(this.spRepBlocksPerRow());
    }

    let data = await datasetsApi.generatePlots(requestData);
    this.checkPlotGeneration(data);

    return data;
  }

  private checkPlotGeneration(data: datasetsApi.PlotGenerationData) {
    if (data.separate_timeout) {
      this.trial().separateTestSubjects(false);
      confirmDialog(
        i18n.t('Warning')(),
        i18n.t("It's impossible to separate the test subjects for this layout.")(),
        undefined,
        true
      );
    }
  }

  /**
   * Ask the user to confirm a change that recreates the plots.
   *
   * @param leadWarnings  consequences of the change to state before the plot warning, one paragraph
   *     each. When given, the dialog is always shown, even for a change that can safely recreate the
   *     plots.
   */
  confirmChangeAffectingPlots(
    itemName: string,
    forceWarning = false,
    leadWarnings: string[] = []
  ): Promise<{}> {
    const plotWarning = this.getPlotChangeWarning(itemName, forceWarning);

    if (leadWarnings.length === 0 && !plotWarning) {
      this.forceRegeneratePlots('full');
      return Promise.resolve(null);
    }

    const body = [...leadWarnings, plotWarning || i18n.t('Are you sure you want to continue?')()];

    const title = i18n.t('Changing {{ itemName }}', { itemName })();

    return confirmDialog(title, body).then((): null => {
      this.forceRegeneratePlots('full');
      return null;
    });
  }

  /**
   * @param leadWarnings  consequences to state before the treatments and plot warnings
   */
  confirmChangeResettingTreatments(
    itemName: string,
    forceWarning = false,
    leadWarnings: string[] = []
  ): Promise<{}> {
    const treatmentsWarning = i18n.t(
      'You have already added treatments. Changing the {{ item }} will remove all treatments. ',
      { item: itemName }
    )();

    return this.confirmChangeAffectingPlots(itemName, forceWarning, [...leadWarnings, treatmentsWarning]);
  }

  private getPlotChangeWarning(itemName: string, forceWarning: boolean): string | null {
    const plotsNeedWarning = !this.canSafelyRegeneratePlots() || forceWarning;
    if (!plotsNeedWarning) {
      return null;
    }

    const customizedPlotsMessage = i18n.t('You have customized the plots ordering. ')();
    const nonDraftMessage = i18n.t('This trial is currently active or concluded. ')();
    const messageIntro = this.trial().isNotDraft() ? nonDraftMessage : customizedPlotsMessage;

    return (
      messageIntro +
      i18n.t(
        [
          'change_affecting_plots_warning',
          'Changing the {{ itemName }} will recreate the plots, and some of your customizations might be lost. Are you sure you want to continue?',
        ],
        { itemName }
      )()
    );
  }

  getFirstSubjectDmId(): string {
    if (this.testSubjects().length === 0) {
      return null;
    }
    let ddm = this.testSubjects()[0];
    return ddm.dimensionMeta() ? ddm.dimensionMeta().id() : null;
  }

  private getCustomerDimensionDmIds(): string[] {
    if (this.trial().hasManualDesign()) {
      return this.testSubjects()
        .filter((ddm) => !!ddm.dimensionMeta())
        .map((ddm) => ddm.dimensionMeta().id());
    } else if (this.trial().hasCustomerDesign()) {
      return [this.getFirstSubjectDmId()];
    } else {
      return [];
    }
  }

  /**
   * @param notifyRemovedTreatments  pass false when the user has already been told that the
   *     treatments go away, so the same removal is not reported twice
   */
  onTestSubjectsUpdated(notifyRemovedTreatments = true) {
    this.removeInvalidCustomerDimensions();
    this.updateSPMainDim(this.testSubjects());
    this.pruneTreatmentsWithInvalidFactors(notifyRemovedTreatments);
  }

  // Removes treatments whose factors reference a dimension element that is no longer part of any
  // test subject's limit_to. This keeps the treatments in sync when a test subject's dimensions are
  // narrowed, and notifies the user about any treatment removed as a side effect, unless the caller
  // has already announced the removal.
  pruneTreatmentsWithInvalidFactors(notifyRemovedTreatments = true) {
    const validDimensionIds = new Set<string>();
    for (const datasetDimensionMeta of this.testSubjects()) {
      datasetDimensionMeta.limitTo().forEach((limitTo) => validDimensionIds.add(limitTo.id()));
    }

    const remainingTreatments = this.treatments().filter((treatment) =>
      treatment.factors().every((factor) => validDimensionIds.has(factor.dimension().id()))
    );

    const removedCount = this.treatments().length - remainingTreatments.length;
    if (removedCount > 0) {
      this.treatments(remainingTreatments);

      if (!notifyRemovedTreatments) {
        return;
      }

      infoDialog(
        i18n.t(['treatments_removed_title', 'Treatments removed'])(),
        i18n.t(
          [
            'treatments_removed_invalid_factors',
            '{{count}} treatment(s) were removed because they referenced a test subject element that is no longer available.',
          ],
          { count: removedCount }
        )()
      );
    }
  }

  onPlotDesignChanged() {
    this.removeInvalidCustomerDimensions();
  }

  private removeInvalidCustomerDimensions() {
    let validDmIds = this.getCustomerDimensionDmIds();
    this.customerDimensions(
      this.customerDimensions().filter((dim) => validDmIds.indexOf(dim.dimension_meta_id) >= 0)
    );
  }

  sortedVisits = ko.pureComputed<{ visit: ScheduledVisit; idx: number }[]>(() => {
    let visits = this.scheduledVisits().map((visit, idx) => ({
      visit,
      idx,
      rank: rankVisitOnTimeline(visit),
    }));
    visits.sort((a, b) => {
      for (let term = 0; term < a.rank.length; term++) {
        if (a.rank[term] !== b.rank[term]) {
          return a.rank[term] - b.rank[term];
        }
      }
      return a.idx - b.idx;
    });

    return visits.map(({ visit, idx }) => ({ visit, idx }));
  });

  visitLeftPerc(item: { visit: ScheduledVisit; idx: number }, idx: number): string {
    let days = parseInt(item.visit.estDaysAfterPlanting(), 10);
    if (isNaN(days)) {
      return '1';
    }

    let prev = this.sortedVisits()[idx - 1];
    if (prev) {
      let prevDays = parseInt(prev.visit.estDaysAfterPlanting(), 10);
      if (!isNaN(prevDays)) {
        return (days - prevDays).toString();
      }
    }

    return days.toString();
  }

  isVisitSelected(item: { visit: ScheduledVisit; idx: number }): boolean {
    return this.scheduledVisitsConfig.selectedEntity() === item.visit;
  }

  blockWidth() {
    let design = this.trial().plotDesign();
    let rcbType = this.trial().rcbType();

    if (design === 'rcb') {
      if (rcbType === 'rcb_vertical') {
        return 1;
      } else if (rcbType === 'rcb_horizontal') {
        return this.plotsPerReplication();
      } else if (rcbType === 'rcb_rect') {
        let n = parseInt(this.trial().rcbBlockWidth(), 10);
        if (!isNaN(n) && n > 0) {
          return n;
        }
      }
    } else if (design === 'latin_square') {
      let factors = this.testSubjects();
      let last = factors[factors.length - 1];
      if (last && last.limitTo().length > 0) {
        return last.limitTo().length;
      }
    } else if (design === 'split_plot' && this.hasSplitPlotBlocksPerRow()) {
      for (let factor of this.testSubjects()) {
        if (
          factor.dimensionMeta() &&
          factor.dimensionMeta().id() === this.spMainDMId() &&
          factor.limitTo().length > 0
        ) {
          return factor.limitTo().length;
        }
      }
    } else if (design === 'default' || design === 'manual') {
      return 1;
    } else if (design === 'alpha_lattice') {
      let blockSize = parseInt(this.alBlockSize(), 10);
      if (isNaN(blockSize) || blockSize <= 0) {
        return Number.MAX_VALUE;
      }
      let perRow = parseInt(this.alPlotsPerBlockRow(), 10);
      if (!isNaN(perRow) && perRow > 0) {
        return Math.min(perRow, blockSize);
      } else {
        return blockSize;
      }
    }

    return Number.MAX_VALUE;
  }

  blockHeight() {
    let design = this.trial().plotDesign();
    let rcbType = this.trial().rcbType();

    if (design === 'rcb') {
      if (rcbType === 'rcb_horizontal') {
        let rowLength = parseInt(this.trial().rowLength(), 10);
        if (isNaN(rowLength) || rowLength < 1) {
          rowLength = this.replications() || 1;
        }
        let blockSize = this.plotsPerReplication();
        return Math.max(1, Math.floor(blockSize / rowLength));
      } else if (rcbType === 'rcb_rect') {
        let n = parseInt(this.trial().rcbBlockWidth(), 10);
        if (!isNaN(n) && n > 0) {
          return Math.ceil(this.plotsPerReplication() / n);
        }
      }
    } else if (design === 'latin_square') {
      return this.blockWidth();
    } else if (design === 'split_plot' && this.hasSplitPlotBlocksPerRow()) {
      let n = 1;
      for (let factor of this.testSubjects()) {
        if (factor.dimensionMeta() && factor.dimensionMeta().id() !== this.spMainDMId()) {
          n *= factor.limitTo().length;
        }
      }

      if (n > 0) {
        return n;
      }
    } else if (design === 'alpha_lattice') {
      let blockSize = parseInt(this.alBlockSize(), 10);
      if (isNaN(blockSize) || blockSize <= 0) {
        return Number.MAX_VALUE;
      }
      let perRow = parseInt(this.alPlotsPerBlockRow(), 10);
      if (isNaN(perRow) || perRow <= 0 || perRow > blockSize) {
        perRow = blockSize;
      }

      return Math.ceil(blockSize / perRow);
    }

    return Number.MAX_VALUE;
  }

  blockWrap() {
    return this.trial().plotDesign() === 'rcb' && this.trial().rcbType() === 'rcb_horizontal';
  }

  private plotsPerReplication(): number {
    return this.testSubjects().reduce((acc, factor) => Math.max(1, factor.limitTo().length) * acc, 1);
  }

  private hasSplitPlotBlocksPerRow(): boolean {
    return parseInt(this.spRepBlocksPerRow(), 10) > 0;
  }

  async applyCustomLayout(data: CustomLayoutData): Promise<string[]> {
    const trial = this.trial();
    let errors: string[] = [];

    if (!trial.isDraft()) {
      // validate that no dimension or site has been removed

      const presentDimIds = new Set<string>();
      for (let dims of data.ddm_dimensions) {
        for (let dim of dims) {
          presentDimIds.add(dim.id);
        }
      }
      for (let siteId of data.site_ids) {
        presentDimIds.add(siteId);
      }

      for (let ddm of this.testSubjects()) {
        for (let dim of ddm.limitTo()) {
          if (!presentDimIds.has(dim.id())) {
            errors.push(
              i18n.t("The trial is not a draft. You can't remove {{dim}}.", {
                dim: translate(dim.nameJson()),
              })()
            );
          }
        }
      }
      for (let site of this.sites()) {
        if (!presentDimIds.has(site.id())) {
          errors.push(
            i18n.t("The trial is not a draft. You can't remove {{dim}}.", {
              dim: translate(site.nameJson()),
            })()
          );
        }
      }
    }

    if (errors.length > 0) {
      return errors;
    }

    const sites = data.site_ids.length > 0 ? await sitesApi.listIds(data.site_ids) : [];

    this.treatments(data.treatments.map((treatmentData) => new Treatment(treatmentData)));
    this.rebindApplicationsToTreatments();

    let i = 0;
    for (let ddm of this.testSubjects()) {
      ddm.limitTo(
        data.ddm_dimensions[i].map((dimData) => new LimitToDimension(this, ddm.dimensionMeta(), dimData))
      );
      i++;
    }

    this.applyingImportedLayout = true;
    this.sites(sites.map((site) => new LimitToDimension(this, this.siteDM, site)));
    this.replications(data.replications);
    this.plotGuides(null);
    // The trial can already have guides persisted by an earlier save (a
    // template copy without reset keeps the template's guides). The imported
    // layout supersedes them, and after the import they become invisible to
    // the wizard (guides whose dimensions are no longer in the limit-to sets
    // are dropped from the wizard payload), so saving null guides here is the
    // only way to delete them.
    this.savePlotGuides = true;

    this.trial().rowLength('');
    this.trial().plotDesign('manual');
    this.forceRegeneratePlots('no');

    this.setPlots(data.plot_list_data, true);
    this.customPlotPosition(false);
    this.customPlotNumbers(false);
    this.hasImportedLayout(true);
    this.applyingImportedLayout = false;

    return [];
  }
}

const defaultResolutionIndex = 1; // medium resolution

// NOTE: must be kept in sync with Dataset.SUPPORTED_RESOLUTIONS on the backend
const supportedResolutions = [
  {
    id: '1',
    get name() {
      return i18n.t('Small')() + ' (320x240)';
    },
    width: 320,
    height: 240,
  },
  {
    id: '2',
    get name() {
      return i18n.t('Medium')() + ' (640x480)';
    },
    width: 640,
    height: 480,
  },
  {
    id: '3',
    get name() {
      return i18n.t('Large')() + ' (800x600)';
    },
    width: 800,
    height: 600,
  },
  {
    id: '4',
    get name() {
      return i18n.t('Extra Large')() + ' (1600x1200)';
    },
    width: 1600,
    height: 1200,
  },
  {
    id: '6',
    get name() {
      return i18n.t('Full HD')() + ' (1920x1440)';
    },
    width: 1920,
    height: 1440,
  },
  {
    id: '7',
    get name() {
      return i18n.t('4K')() + ' (3840x2880)';
    },
    width: 3840,
    height: 2880,
  },
  {
    id: '8',
    get name() {
      return i18n.t('6K')() + ' (6144x4608)';
    },
    width: 6144,
    height: 4608,
  },
  {
    id: '9',
    get name() {
      return i18n.t('8K')() + ' (7680x5760)';
    },
    width: 7680,
    height: 5760,
  },
  {
    id: '5',
    get name() {
      return i18n.t('Full size')();
    },
    width: 1601,
    height: 1200,
  },
];

function getResolutionId(data: { image_resolution_width: number; image_resolution_height: number }): string {
  for (let resolution of supportedResolutions) {
    if (
      resolution.width == data.image_resolution_width &&
      resolution.height == data.image_resolution_height
    ) {
      return resolution.id;
    }
  }

  return null;
}

function toResolutionData(id: string): {
  image_resolution_width: number;
  image_resolution_height: number;
} {
  let resolution = supportedResolutions[defaultResolutionIndex];

  for (const res of supportedResolutions) {
    if (res.id == id) {
      resolution = res;
    }
  }

  return {
    image_resolution_width: resolution.width,
    image_resolution_height: resolution.height,
  };
}

export class DatasetWizard {
  private slugGenerator: SlugGenerator;

  supportedResolutions = supportedResolutions;
  resolutionId = ko.observable(supportedResolutions[defaultResolutionIndex].id);

  id = ko.observable<string>(null);
  private copiedFromId: string = null;
  nameJson = ko.observable<I18nText>().extend({
    i18nTextRequired: true,
    serverError: true,
  });
  order = ko.observable(1);
  skipDimensionSelection = ko.observable(false);
  forceSiteSelection = ko.observable(false);
  containsManagementData = ko.observable(false);
  canAddFact = ko.observable(false);
  nameSlug = ko.observable('').extend(slugValidation);
  measurementMetas = ko.observableArray<MeasurementMeta>();
  requiredDatasetDimensionMetas = ko.observableArray<DatasetDimensionMeta>();

  plotDMSelections = ko.observableArray<DimensionMetaSelection>();
  orderedMeasurementMetas = new OrderedEntities(this.measurementMetas);
  errors = ko.validation.group(this, { deep: false });

  otherDDMConfig: DatasetDimensionMetasEditConfiguration;

  // mms minus legacy free ddm
  properMeasurementMetas = ko.pureComputed(() => this.measurementMetas().filter((mm) => !mm.isDDMEntity));

  private preferEmbeddedMT = session.tenant().prefer_embedded_mt;

  private subscriptions: KnockoutSubscription[] = [];

  constructor(
    private trialWizard: TrialWizard,
    data?: DatasetWizardData,
    defaultName?: string
  ) {
    let trial = trialWizard.trial();
    let allowEditAny = canEditTrial(trialWizard.userData, trial);

    this.otherDDMConfig = {
      user: this.trialWizard.userData,

      addDMText: i18n.t('Add entity'),
      selectDMText: i18n.t('Select'),
      emptyDDMWarning: '',
      emptyDimensionsWarning: '',
      addDimensionText(ddm: DatasetDimensionMeta) {
        return i18n.t('Add selectable record')();
      },

      allowAnonymize: true,
      allowEditOptional: true,
      trialWizard: trialWizard,
      canEditRequired: () => false,
      canEditDimensionMeta: (ddm: DatasetDimensionMeta) => true,
      allowAdd: () => allowEditAny,
      allowEdit: ko.pureComputed(() => {
        let trial = this.trialWizard.trial();
        return allowEditAny && (!this.id() || trial.isDraft());
      }),
      allowEditAny: ko.pureComputed(() => {
        return allowEditAny;
      }),
      allowRemove: (ddm: DatasetDimensionMeta) => {
        return !ddm.id() || this.trialWizard.trial().isDraft();
      },

      datasetDimensionMetas: this.requiredDatasetDimensionMetas,
      createDatasetDimensionMeta: () => {
        return new DatasetDimensionMeta(this.trialWizard);
      },

      confirmEditEntity: () => {
        return Promise.resolve(null);
      },
      confirmChangeEntities: () => {
        return Promise.resolve(null);
      },
      allowReorder: allowEditAny,
      allowViewLimitToDimensionNameAndAttributes: allowViewLimitToDimensionNameAndAttributes(),
    };

    if (data) {
      let trial = trialWizard.trial();

      this.resolutionId(getResolutionId(data));

      this.id(data.id);
      this.copiedFromId = data.copied_from_id;
      this.nameJson(data.name_json);
      this.order(data.order);
      this.skipDimensionSelection(data.skip_dimension_selection);
      this.forceSiteSelection(data.force_site_selection);
      this.containsManagementData(data.contains_management_data);
      this.canAddFact(data.can_add_fact);
      this.nameSlug(data.name_slug);

      let order = 0;
      let mms = data.measurement_dataset_dimension_metas.map((d) => {
        let mm = MeasurementMeta.fromDDM(trial, new DatasetDimensionMeta(trialWizard, d));
        mm.order(order);
        order++;

        return mm;
      });
      this.measurementMetas(
        mms.concat(
          data.measurement_metas.map((d) => {
            let mm = new MeasurementMeta(
              {
                allowDimensionMeta: true,
                management: false,
                requireMeasurementType: !this.preferEmbeddedMT,
                validateLimitTo: this.mmLimitToRequired(),
              },
              trial,
              d
            );
            mm.order(mm.order() + order);
            return mm;
          })
        )
      );
      this.orderedMeasurementMetas = new OrderedEntities(this.measurementMetas);
      this.requiredDatasetDimensionMetas(
        data.required_dataset_dimension_metas.map((d) => new DatasetDimensionMeta(trialWizard, d))
      );
      this.updatePlotDDMSelections(data.excluded_plot_dm_ids);
    } else {
      this.nameJson(asI18nText(defaultName || i18n.t('Trait group')()));
      this.updatePlotDDMSelections([]);
    }

    this.subscriptions.push(this.nameJson.subscribe(this.onNameChanged));
    this.subscriptions.push(
      trialWizard.testSubjects.subscribe(() => {
        this.updatePlotDDMSelections();
      })
    );
    this.subscriptions.push(
      trialWizard.sites.subscribe(() => {
        this.updatePlotDDMSelections();
      })
    );

    this.subscriptions.push(
      this.measurementMetas.subscribe(() => {
        this.trialWizard.checkTPPTraits();
      })
    );

    this.slugGenerator = new SlugGenerator(this.nameJson, null, this.nameSlug, {
      canEdit: !this.id() || this.trialWizard.trial().isDraft(),
      fillIfEmpty: true,
    });
  }

  dipose() {
    for (let subscription of this.subscriptions) {
      subscription.dispose();
    }
    this.slugGenerator.dispose();
  }

  nameOrError = ko.pureComputed(() => {
    let name = this.nameJson();
    if (!translate(name).trim()) {
      return i18n.t('Missing group name')();
    } else {
      return name;
    }
  });

  getValidationTarget(): {} {
    let target = ko.utils.extend({}, this);

    // avoid recursion, which would ignore getValidationTarget() for the wizard
    delete (<any>target)['trialWizard'];

    return target;
  }

  addEmptyMeasurementMeta() {
    return this.addMeasurementMeta(undefined);
  }

  addMeasurementMeta(measurementMetaData: datasetsApi.MeasurementMetaData) {
    let measurementMeta = new MeasurementMeta(
      {
        allowDimensionMeta: true,
        management: false,
        requireMeasurementType: !this.preferEmbeddedMT,
        validateLimitTo: this.mmLimitToRequired(),
      },
      this.trialWizard.trial(),
      measurementMetaData
    );
    if (this.hasExcludedPlotDM()) {
      // default to false when this is not a plot dataset
      measurementMeta.includeInSummary(false);
    }
    this.orderedMeasurementMetas.add(measurementMeta);

    return measurementMeta;
  }

  private mmLimitToRequired() {
    return APP_CONFIG.MM_LIMIT_TO_REQUIRED && !this.trialWizard.trial().template;
  }

  private onNameChanged = () => {
    this.nameJson.serverError('');
  };

  private updatePlotDDMSelections(initialValue?: string[]) {
    if (!initialValue) {
      initialValue = this.getExcludePlotDMIds();
    }

    this.plotDMSelections(
      this.trialWizard.testSubjects().map((ddm) => {
        return new DimensionMetaSelection(ddm.dimensionMeta());
      })
    );
    if (this.trialWizard.sites().length > 0) {
      this.plotDMSelections.push(new DimensionMetaSelection(this.trialWizard.siteDM));
    }
    this.plotDMSelections.push(new DimensionMetaSelection(this.trialWizard.replicationDM));

    for (let sel of this.plotDMSelections()) {
      if (sel.dimensionMeta && initialValue.indexOf(sel.dimensionMeta.id()) !== -1) {
        sel.include(false);
      }
    }
  }

  hasExcludedPlotDM(): boolean {
    return this.getExcludePlotDMIds().length > 0;
  }

  private getExcludePlotDMIds(): string[] {
    return this.plotDMSelections()
      .filter((sel) => !sel.include())
      .map((sel) => sel.dimensionMeta.id());
  }

  hasSites = ko.pureComputed(() => {
    if (this.trialWizard.sites().length === 0) {
      return false;
    }

    for (let sel of this.plotDMSelections()) {
      if (sel.dimensionMeta && sel.dimensionMeta.slug() === SITE_SLUG && !sel.include()) {
        return false;
      }
    }

    return true;
  });

  hasErrors() {
    if (this.errors().length > 0 || this.nameJson.serverError() || this.nameSlug.serverError()) {
      return true;
    }

    for (let ddm of this.requiredDatasetDimensionMetas()) {
      if (ddm.hasErrors()) {
        return true;
      }
    }

    for (let mm of this.measurementMetas()) {
      if (mm.hasErrors()) {
        return true;
      }
    }

    return false;
  }

  showErrors() {
    this.errors.showAllMessages();

    for (let ddm of this.requiredDatasetDimensionMetas()) {
      ddm.showErrors();
    }

    for (let mm of this.measurementMetas()) {
      mm.showErrors();
    }
  }

  toData(isMasterDataset: boolean): DatasetWizardData {
    let order = 0;
    for (let ddm of this.requiredDatasetDimensionMetas()) {
      ddm.required(true);
      ddm.useForPlot(false);
      ddm.order(order);
      order++;
    }

    return {
      id: this.id(),
      copied_from_id: this.copiedFromId,
      name_json: this.nameJson(),
      order: this.order(),
      skip_dimension_selection: this.skipDimensionSelection(),
      force_site_selection: this.forceSiteSelection(),
      contains_management_data: this.containsManagementData(),
      can_add_fact: this.canAddFact(),
      name_slug: this.nameSlug(),
      is_master_dataset: isMasterDataset,
      measurement_dataset_dimension_metas: this.measurementMetas()
        .filter((mm) => mm.isDDMEntity)
        .map((mm) => mm.toDatasetDimensionMetaData()),
      measurement_metas: this.measurementMetas()
        .filter((mm) => !mm.isDDMEntity)
        .map((meta) => meta.toData()),
      required_dataset_dimension_metas: this.requiredDatasetDimensionMetas().map((ddm) => ddm.toData(true)),
      excluded_plot_dm_ids: this.getExcludePlotDMIds(),
      ...toResolutionData(this.resolutionId()),
    };
  }
}

class DimensionMetaSelection {
  include = ko.observable(true);

  nameJson = ko.pureComputed(() => {
    if (!this.dimensionMeta) {
      return undefined;
    }

    return this.dimensionMeta.nameJson();
  });

  constructor(public dimensionMeta: DimensionMeta) {}
}

interface ScheduledVisitObservation {
  id: string;
  server_id: string | null;
  ds: ScheduledVisitObservationGrouping;
  mm: ScheduledVisitObservationGroupingItem;
}

interface ScheduledVisitObservationGroupingItem {
  id: KnockoutObservable<string>;
  copiedFromId: string | undefined;
  libraryTemplateId: number | undefined;
  nameJson: KnockoutObservable<I18nText>;
  type: KnockoutObservable<string>;
  order: KnockoutObservable<number>;
}

interface ScheduledVisitObservationGrouping {
  id: KnockoutObservable<string>;
  nameJson: KnockoutObservable<I18nText>;
  order: KnockoutObservable<number>;
  properMeasurementMetas(): ScheduledVisitObservationGroupingItem[];
}

function getScheduledVisitObservationSummaryName(item: ScheduledVisitObservation): string {
  const dsName = translate(item.ds.nameJson());
  const mmName = translate(item.mm.nameJson());

  if (!dsName) {
    return mmName;
  }

  return dsName + ' - ' + mmName;
}

function rankVisitOnTimeline(visit: ScheduledVisit): number[] {
  const days = parseInt(visit.estDaysAfterPlanting(), 10);
  const offset = parseInt(visit.estDaysOffset(), 10);
  const id = parseInt(visit.id ?? '', 10);

  return [
    isNaN(days) ? 1 : 0,
    isNaN(days) ? 0 : days,
    isNaN(offset) ? 1 : 0,
    isNaN(offset) ? 0 : offset,
    isNaN(id) ? Number.MAX_SAFE_INTEGER : id,
  ];
}

export class ScheduledVisit extends ScheduledVisitBase<ScheduledVisitObservation> {
  private nextTempId = 0;
  private tempIds = new Map<string | number | ScheduledVisitObservationGroupingItem, string>();

  templateId: number | undefined;

  disabled = ko.observable(false);

  baseDate = ko.observable<ScheduledVisitObservation>().extend({
    validation: [
      {
        validator: (obs: ScheduledVisitObservation) => {
          return !obs || obs.mm.type() === 'date';
        },
        message: i18n.t('The selected observation must be a date')(),
      },
      {
        validator: (observation: ScheduledVisitObservation | null) =>
          !observation || this.isSelectable(observation),
        message: i18n.t('This trait is not in the trial. Add it back or choose another date trait.')(),
      },
    ],
  });
  observations = ko.observableArray<ScheduledVisitObservation>().extend({ required: false });
  onObservationsChange = () => {
    this.observationsListener.dispose();
    this.observations.sort((a, b) => a.ds.order() - b.ds.order() || a.mm.order() - b.mm.order());
    this.observationsListener = this.observations.subscribe(this.onObservationsChange);
  };
  observationsListener = this.observations.subscribe(this.onObservationsChange);

  mixedSites = ko.pureComputed<boolean>(() => {
    let hasSites: null | true | false = null;
    for (let item of this.observations()) {
      let itemHasSites = this.hasSites(item.ds, item.mm);
      if (hasSites === null) {
        hasSites = itemHasSites;
      } else if (hasSites !== itemHasSites) {
        return true;
      }
    }

    return false;
  });

  errors = ko.validation.group(
    [this.nameJson, this.code, this.baseDate, this.days, this.daysAfterPlanting, this.observations],
    { deep: false }
  );

  datasetIndex = ko.observable('0');
  datasetOptions = ko.pureComputed(() => {
    return this.groups().map((ds, idx) => ({
      idx: idx.toString(),
      name: translate(ds.nameJson()),
      ds,
    }));
  });

  observationsSearchConfig: FormSelectSearchConfiguration<ScheduledVisitObservation> = {
    enableAddAll: true,
    list: (params) =>
      Promise.resolve(this.selectableObservations(params, (dsIdx) => dsIdx === this.datasetIndex())),
    entities: this.observations,
    getSummaryName: getScheduledVisitObservationSummaryName,
    getSelectedEntityIcon: (item) =>
      item.mm.type() === 'derived' ? { name: 'calculate', title: i18n.t('Derived trait')() } : null,
  };

  constructor(
    private editMode: EditMode,
    private groups: ko.ObservableArray<ScheduledVisitObservationGrouping>,
    private hasSites: (
      ds: ScheduledVisitObservationGrouping,
      mm: ScheduledVisitObservationGroupingItem
    ) => boolean,
    data?: ScheduledVisitData,
    // relevant only for new scheduled visits not saved yet (includes new trials from templates, i.e. data is not necessarily empty)
    source?: {
      templateId: number;
    }
  ) {
    super();

    this.templateId = source?.templateId;

    this.finishSetup(
      {
        list: (params) =>
          Promise.resolve(this.selectableObservations(params, (dsIdx, mm) => mm.type() === 'date')),
        entity: this.baseDate,
        getSummaryName: getScheduledVisitObservationSummaryName,
      },
      data
    );

    if (data) {
      let mms: {
        [key: string]: {
          id: string;
          ds: ScheduledVisitObservationGrouping;
          mm: ScheduledVisitObservationGroupingItem;
        };
      } = {};
      for (let ds of groups()) {
        for (let mm of ds.properMeasurementMetas()) {
          let id = this.tempIdFor(mm);
          mms[mm.id() || mm.copiedFromId] = { id, ds, mm };
        }
      }

      this.disabled(data.disabled);

      // Copying a library trial from a template leaves its non-library traits out of `mms`, and
      // an entry built from the absent trait has no `ds` or `mm`, so the first sort or validation
      // that reads through them throws.
      const baseDate = data.base_date_id ? mms[data.base_date_id] : undefined;
      if (data.base_date_id && !baseDate) {
        console.warn('Cleared a scheduled visit base date with no trait on offer:', data.base_date_id);
      }
      this.baseDate(baseDate ? { server_id: null, ...baseDate } : null);

      let obs = data.observations.slice();
      obs.sort((a, b) => a.order - b.order);
      const resolvable = obs.filter((observation) => !!mms[observation.measurement_meta_id]);
      if (resolvable.length !== obs.length) {
        console.warn(
          'Dropped scheduled visit observations with no trait on offer:',
          obs
            .filter((observation) => !mms[observation.measurement_meta_id])
            .map((observation) => observation.measurement_meta_id)
        );
      }
      this.observations(
        resolvable.map((observation) => ({
          server_id: observation.id,
          ...mms[observation.measurement_meta_id],
        }))
      );
    }
  }

  private tempIdFor(mm: ScheduledVisitObservationGroupingItem) {
    // in library mode, libraryTemplateId || mm.id() is a stable identifier for both saved and unsaved mm, while the mm instance identity is not a stable identifier
    // in manual mode, libraryTemplateId is empty, mm.id() if present is a stable identifier, and the mm instance identity is also a stable identifier
    const identity = this.editMode === 'library' ? mm.libraryTemplateId || mm.id() : mm;

    if (!this.tempIds.has(identity)) {
      this.nextTempId++;
      this.tempIds.set(identity, this.nextTempId.toString());
    }

    return this.tempIds.get(identity);
  }

  private isSelectable(observation: ScheduledVisitObservation): boolean {
    const identity = this.tempIdFor(observation.mm);
    return this.selectableObservations({ limit: Number.MAX_SAFE_INTEGER }, () => true).some(
      (selectable) => selectable.id === identity
    );
  }

  disable(): void {
    this.disabled(!this.disabled());
  }

  public selectableObservations(
    params: ListRequestParams,
    filter: (dsIdx: string, mm: ScheduledVisitObservationGroupingItem) => boolean
  ): ScheduledVisitObservation[] {
    let items: ScheduledVisitObservation[] = [];
    for (let opt of this.datasetOptions()) {
      for (let mm of opt.ds.properMeasurementMetas()) {
        if (
          filter(opt.idx, mm) &&
          (!params.name_prefix ||
            translate(mm.nameJson()).toLocaleLowerCase().indexOf(params.name_prefix.toLocaleLowerCase()) >
              -1)
        ) {
          let id = this.tempIdFor(mm);
          items.push({ id, server_id: null, ds: opt.ds, mm });
        }
      }
    }

    let offset = params.offset || 0;
    return items.slice(offset, offset + params.limit);
  }

  onDatasetRemoved(ds: ScheduledVisitObservationGrouping) {
    if (this.datasetOptions().every((ds) => ds.idx !== this.datasetIndex())) {
      this.datasetIndex('0');
    }
    if (this.baseDate()?.ds === ds) {
      this.baseDate(null);
    }
    this.observations(this.observations().filter((obs) => obs.ds !== ds));
  }

  onMeasurementMetaRemoved(mm: ScheduledVisitObservationGroupingItem) {
    if (this.baseDate()?.mm === mm) {
      this.baseDate(null);
    }
    this.observations(this.observations().filter((obs) => obs.mm !== mm));
  }

  toData(order: number): ScheduledVisitData {
    let mms = new Map<ScheduledVisitObservationGroupingItem, number>();
    // don't use _idx field in library mode
    if (this.editMode === 'manual') {
      let idx = 0;
      for (let ds of this.groups()) {
        for (let mm of ds.properMeasurementMetas()) {
          mms.set(mm, idx++);
        }
      }
    }

    const baseDateMM = this.baseDate()?.mm;

    return {
      ...super.toData(order),
      template_id: this.templateId ?? null,
      disabled: this.disabled(),
      base_date_idx: mms.get(baseDateMM) ?? null,
      base_date_library_measurement_meta_id: baseDateMM?.libraryTemplateId,
      observations: this.observations().map((obs, idx) => ({
        id: obs.server_id,
        measurement_meta_idx: mms.get(obs.mm),
        library_measurement_meta_id: obs.mm.libraryTemplateId,
        order: idx,
      })),
    };
  }

  hasErrors() {
    return this.errors().length > 0 || this.mixedSites() || !!this.code.serverError();
  }

  showErrors() {
    this.errors.showAllMessages();
  }

  groupedObservations = ko.pureComputed(() => {
    let grouped: { [key: string]: ScheduledVisitObservation[] } = groupBy(
      this.observations(),
      (obs) => obs.ds.id(),
      accList
    );

    return this.groups()
      .filter((ds) => !!grouped[ds.id()])
      .map((ds) => {
        const observations: ScheduledVisitTraitData[] = grouped[ds.id()].map((obs) => ({
          mm_id: obs.mm.id(),
          mm_library_template_id: obs.mm.libraryTemplateId,
          name: obs.mm.nameJson(),
          order: obs.mm.order(),
          ds_id: ds.id(),
          ds_name: ds.nameJson(),
        }));

        return {
          nameJson: ds.nameJson(),
          observations,
        };
      });
  });

  popupTitle = ko.pureComputed(() => {
    return translate(this.nameJson()) || '—';
  });
}

export function expandWithPeriodicVisits<T extends ScheduledVisitPreviewData | ScheduledVisitData>(
  preview: T,
  isTemplate: boolean
): (T & { is_periodic_repetition?: boolean })[] {
  const repetitionNumberOfDays = asInt(preview.repetition_number_of_days);
  const maximumNumberOfRepetitions = asInt(preview.maximum_number_of_repetitions);
  const daysAfterPlanting = asInt(preview.days_after_planting);
  const daysOffset = asInt(preview.days_offset);

  // don't add periodic scheduled visits to templates
  if (isTemplate || !preview.repetition_number_of_days || !maximumNumberOfRepetitions) {
    return [preview];
  }

  const res: (T & { is_periodic_repetition?: boolean })[] = [];

  for (let index = 1; index <= maximumNumberOfRepetitions; index++) {
    const copy: (ScheduledVisitPreviewData | ScheduledVisitData) & { is_periodic_repetition?: boolean } = {
      ...preview,
      name_json: transformI18Text(preview.name_json, (name) => `${name} (${index})`),
      code: index === 1 ? preview.code : `${preview.code}_${index}`,
      n_max_visits: 1,
      days_offset: (
        'base_date_observation' in preview
          ? preview.base_date_observation
          : preview.base_date_id || preview.base_date_idx != null
      )
        ? (daysOffset || 0) + repetitionNumberOfDays * (index - 1)
        : null,
      days_after_planting:
        daysAfterPlanting == null || repetitionNumberOfDays == null
          ? null
          : daysAfterPlanting + repetitionNumberOfDays * (index - 1),
      is_periodic_repetition: index > 1,
      template_id: index === 1 ? preview.template_id : undefined,
    };

    res.push(copy as T);
  }

  return res;
}

export class TrialInputApplication {
  serverId: number | null = null;
  productId = ko.observable('');
  productName = ko.observable('');
  productDimensionMetaId = ko.observable('');
  concentration = ko.observable('');
  concentrationUnitId = ko.observable<number | null>(null);
  concentrationUnitName = ko.observable('');
  // the unit category slug: one product must be planned in a single category
  // throughout the trial (see input_application_unit_categories.ts)
  concentrationUnitCategory = ko.observable('');
  applicationMethodId = ko.observable<number | null>(null);
  applicationMethodName = ko.observable('');

  constructor(
    public scheduledVisit: ScheduledVisit,
    // the wizard treatment the application belongs to (the row)
    public treatment: Treatment,
    data?: TrialInputApplicationData
  ) {
    if (data) {
      this.serverId = data.id;
      this.productId(data.product_id);
      this.productName(data.product_name ?? '');
      this.productDimensionMetaId(data.product_dimension_meta_id ?? '');
      this.concentration(data.concentration.toString());
      this.concentrationUnitId(data.concentration_unit_id);
      this.concentrationUnitName(data.concentration_unit_name ?? '');
      this.concentrationUnitCategory(data.concentration_unit_category ?? '');
      this.applicationMethodId(data.application_method_id);
      this.applicationMethodName(data.application_method_name ?? '');
    }
  }

  /** "Nitrogen 2 kg_ha (Foliar spray)" — the summary shown in a table cell. */
  summary(): string {
    const parts = [this.productName(), this.concentration(), this.concentrationUnitName()].filter(
      (part) => !!part
    );
    const method = this.applicationMethodName();

    return method ? `${parts.join(' ')} (${method})` : parts.join(' ');
  }

  toData(scheduledVisitIndex: number, treatmentIndex: number): TrialInputApplicationData {
    return {
      id: this.serverId,
      scheduled_visit_index: scheduledVisitIndex,
      treatment_index: treatmentIndex,
      product_id: this.productId(),
      concentration: parseFloat(this.concentration()),
      concentration_unit_id: this.concentrationUnitId(),
      application_method_id: this.applicationMethodId(),
    };
  }
}

export class TrialTraits {
  loaded = ko.observable(false);
  selectedTraits = ko.observableArray<SelectedTrait>();
  needsLoadLibrarySV: TraitEditAction[] = []; // tells if the svs need to be loaded to validate the visits
  // A trait was saved/added/removed since the last successful wizard save. Counts as a
  // structural change for shouldRederiveLibraryStructure (a trait save goes through its
  // own endpoint, so the wizard payload diff can't detect it). Reset after the trial is
  // reloaded following a successful wizard save.
  didUpdateTrait = false;

  // keep errors in a separate map, so they're preserved
  // when reloading the traits
  // mm id -> error
  private errors = new Map<string, ko.Observable<string>>();

  constructor(private initialTraits: TrialTraitData[]) {}

  async load() {
    if (this.loaded()) {
      return;
    }

    this.selectedTraits([]);

    const selectedTraits: SelectedTrait[] = [];

    if (this.initialTraits.length > 0) {
      const mms = await listTrialLibrary({
        mm_ids: this.initialTraits.map((t) => t.mm_id),
      });
      const byId = new Map<string, SelectedTraitData>();
      for (const mm of mms) {
        byId.set(mm.id, mm);
      }

      for (const trait of this.initialTraits) {
        const mm = byId.get(trait.mm_id);
        if (mm) {
          // traits won't be saved if this trial is created from a template and not saved yet.
          const isSaved = trait.mm_id !== trait.mm_template_id;
          selectedTraits.push(selectedTraitFromMM(mm, isSaved));
          if (!this.errors.has(trait.mm_id)) {
            this.errors.set(trait.mm_id, ko.observable(''));
          }
        }
      }
    }

    this.selectedTraits(selectedTraits);
    this.loaded(true);
  }

  applyServerErrors(errors: any) {
    const traitErrors = errors?.['traits'];
    if (traitErrors) {
      this.selectedTraits().forEach((t, i) => {
        this.errors.get(t.mmId)(traitErrors[i] ?? '');
      });
    }
  }

  traitError(trait: SelectedTrait): string {
    const error = this.errors.get(trait.mmId);

    // A trait row that outlived a wizard rebuild can still evaluate against this
    // instance, which never loaded its id. Not reproduced locally; reported so the next
    // occurrence shows the page state.
    if (!error) {
      const isOwnTrait = this.selectedTraits.peek().includes(trait);
      logError(
        `TrialTraits.traitError: unknown trait id ${trait.mmId} (own trait: ${isOwnTrait}, ` +
          `selected: ${this.selectedTraits.peek().length}, rows: ` +
          `${document.querySelectorAll('.selected-traits-body tr[selected-trait-id]').length})`,
        null
      );
      return '';
    }

    return error();
  }

  hasErrors(): boolean {
    return this.selectedTraits().some((t) => !!this.errors.get(t.mmId)());
  }

  remove = (trait: SelectedTrait) => {
    this.selectedTraits.remove(trait);
    this.needsLoadLibrarySV.push({ forTrait: trait, action: 'remove' });
    this.didUpdateTrait = true;
    this.errors.set(trait.mmId, ko.observable(''));
  };

  /**
   * @param options.libraryMmId the library trait each entry was copied from, when the entries
   *   are per-trial copies rather than the library traits themselves
   * @param options.isSaved the entries already exist server side
   */
  add(
    selected: datasetsApi.MeasurementMetaData[],
    options: { libraryMmId?: string; isSaved?: boolean } = {}
  ) {
    const validatedMeasurementMetas = [];
    for (let mm of selected) {
      if (!this.errors.has(mm.id)) {
        this.errors.set(mm.id, ko.observable(''));
      }
      const trait = selectedTraitFromMM(mm, options.isSaved ?? false);
      validatedMeasurementMetas.push(trait);
      this.needsLoadLibrarySV.push({
        forTrait: trait,
        action: 'add',
        previewMmId: options.libraryMmId,
      });
      this.didUpdateTrait = true;
    }
    this.selectedTraits.push(...validatedMeasurementMetas);
  }

  async reloadTrait(measurementMetaId: string) {
    const [measurementMeta] = await listTrialLibrary({ mm_ids: [measurementMetaId] });
    const traits = this.selectedTraits.peek();
    const index = traits.findIndex((t) => t.mmId === measurementMetaId);
    const isSaved = traits[index].isSaved;
    this.selectedTraits.splice(index, 1, selectedTraitFromMM(measurementMeta, isSaved));
  }

  toData(): TrialTraitData[] {
    if (this.loaded()) {
      return this.selectedTraits().map((t, i) => ({ mm_id: t.mmId, order: i, disabled: t.disabled() }));
    } else {
      return this.initialTraits.map((t) => ({
        mm_id: t.mm_id,
        order: t.order,
        disabled: t.disabled,
      }));
    }
  }
}

export interface SelectedTrait {
  id?: string; // === mmId; required by form-select-search selections
  isSaved: boolean;
  mmId: string;
  templateId: string;
  // the stable id to reference this trait across library re-derivations
  referenceId: number | null;
  name: I18nText;
  nameSlug: string;
  traitCategoryName: I18nText;
  traitCategoryId: string;
  dataCollectionDetails: string;
  typeName: string;
  type: string;
  tags: string;
  tagsIds: string[];
  scheduledVisitsIds?: string[];
  disabled: ko.Observable<boolean>;
  visibilityToggleTitle: ko.PureComputed<string>;
  collectForSite: boolean;
}

function selectedTraitFromMM(mm: SelectedTraitData, isSaved: boolean): SelectedTrait {
  const disabledObs = ko.observable(mm.disabled ?? false);
  return {
    id: mm.id,
    isSaved,
    mmId: mm.id,
    templateId: mm.template_id,
    // if reference_id is not present, it means mm itself is a library mm
    referenceId: mm.reference_id ?? (parseInt(mm.id, 10) || null),
    name: mm.name_json,
    nameSlug: mm.name_slug,
    traitCategoryName: mm.trait_category?.name_json,
    traitCategoryId: mm.trait_category?.id,
    dataCollectionDetails: mm.data_collection_details ?? '',
    type: mm.type,
    typeName: typeName(mm),
    tags: mm.tags?.map((tag) => translate(tag.name_json))?.join(', ') ?? '',
    tagsIds: mm.tags?.map((tag) => tag.id) ?? [],
    scheduledVisitsIds: mm.scheduled_visits.map((sv) => (sv.template ? sv.template.toString() : sv.id)),
    disabled: disabledObs,
    visibilityToggleTitle: ko.pureComputed(() =>
      disabledObs()
        ? i18n.t(['trait_visibility_toggle_show', 'Show trait'])()
        : i18n.t(['trait_visibility_toggle_hide', 'Hide trait'])()
    ),
    collectForSite: mm.collect_for_site,
  };
}
